import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { tokenStore } from '../lib/tokenStore';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, Shield, Smartphone, Monitor, ChevronLeft, ChevronRight, Users, Calendar, RefreshCw, Send, Gift, Check, X, Newspaper, LifeBuoy, Stethoscope } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function getMonday(d) {
  const date = new Date(d);
  const diff = date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatWeekDate(monday) {
  return monday.toISOString().split('T')[0];
}

function formatDateRange(monday) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const opts = { day: '2-digit', month: '2-digit' };
  return `${monday.toLocaleDateString('de-DE', opts)} - ${sunday.toLocaleDateString('de-DE', opts)}`;
}

export default function AppApiPage() {
  const [tab, setTab] = useState('overview');

  return (
    <div className="space-y-4" data-testid="app-api-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Kunden-App API</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Öffentliche API für die Kunden-App verwalten
        </p>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-lg p-1 w-fit" data-testid="api-tabs">
        {[
          { id: 'overview', label: 'Buchungsübersicht', icon: Calendar },
          { id: 'news', label: 'News', icon: Newspaper },
          { id: 'support', label: 'Support', icon: LifeBuoy },
          { id: 'push', label: 'Push senden', icon: Send },
          { id: 'rewards', label: 'Prämien', icon: Gift },
          { id: 'uebergabe', label: 'Übergabe', icon: RefreshCw },
          { id: 'events', label: 'Events', icon: Calendar },
          { id: 'checkups', label: 'Check-Ups', icon: Stethoscope },
          { id: 'onboarding', label: 'App-Zugänge', icon: Users },
          { id: 'keys', label: 'API-Keys', icon: Key },
          { id: 'docs', label: 'API-Dokumentation', icon: Shield },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <BookingsOverview />}
      {tab === 'push' && <PushBroadcast />}
      {tab === 'news' && <NewsManager />}
      {tab === 'support' && <SupportManager />}
      {tab === 'rewards' && <RewardsManager />}
      {tab === 'uebergabe' && <HandoverManager />}
      {tab === 'events' && <EventsManager />}
      {tab === 'checkups' && <CheckupManager />}
      {tab === 'onboarding' && (
        <div className="space-y-6">
          <OnboardingManager />
          <FamilyHintManager />
        </div>
      )}
      {tab === 'keys' && <ApiKeysManager />}
      {tab === 'docs' && <ApiDocs />}
    </div>
  );
}

// ── Schichtübergabe: Team-Kommunikation zwischen den Schichten ──
// Trainer schreiben und haken in der App ab; hier sieht die Leitung alles
// und kann selbst Anweisungen hinterlegen. Jeder Eintrag löst einen Push aus.
function HandoverManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [typ, setTyp] = useState('info');
  const [wichtig, setWichtig] = useState(false);
  const [kreis, setKreis] = useState('alle');
  const [saving, setSaving] = useState(false);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/admin/handover?status=aktuell`, { headers: getHeaders() });
      if (res.ok) setData(await res.json());
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (text.trim().length < 3) { toast.error('Bitte kurz beschreiben, worum es geht'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/public/admin/handover`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ typ, text: text.trim(), prioritaet: wichtig ? 'wichtig' : 'normal', sichtbar_fuer: kreis }),
      });
      if (res.ok) {
        toast.success(kreis === 'leitung' ? 'Gespeichert — nur für die Leitung sichtbar' : 'Gespeichert — das Team wurde benachrichtigt');
        setText(''); setWichtig(false); setKreis('alle'); load();
      } else toast.error((await res.json())?.detail || 'Fehler beim Speichern');
    } catch { toast.error('Netzwerkfehler'); }
    setSaving(false);
  };

  const remove = async (item) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      const res = await fetch(`${API}/api/public/admin/handover/${item.id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { toast.success('Gelöscht'); load(); }
    } catch { toast.error('Fehler'); }
  };

  const zeit = (iso) => {
    try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="space-y-4" data-testid="handover-manager">
      {/* Anlegen */}
      <div className="border rounded-lg p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-sm">Eintrag für das Team</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Erscheint sofort im Trainer-Bereich der App und löst einen Push an alle Trainer aus.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[['info', 'Notiz'], ['todo', 'Aufgabe']].map(([k, label]) => (
            <button key={k} onClick={() => setTyp(k)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border ${typ === k ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
              {label}
            </button>
          ))}
          <button onClick={() => setWichtig(w => !w)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${wichtig ? 'bg-amber-500/15 text-amber-600 border-amber-500/40' : 'hover:bg-muted'}`}>
            ❗ Wichtig
          </button>
          <span className="text-xs text-muted-foreground ml-2">Sichtbar für:</span>
          {[['alle', 'Ganzes Team'], ['leitung', 'Nur Leitung']].map(([k, label]) => (
            <button key={k} onClick={() => setKreis(k)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border ${kreis === k ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
              {label}
            </button>
          ))}
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={500}
          className="border rounded-md px-3 py-2 text-sm bg-background w-full"
          placeholder={typ === 'todo' ? 'z. B. Sauna-Handtücher nachfüllen' : 'z. B. Mattenreiniger ist leer, Ersatz bestellt'} />
        <button onClick={create} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {typ === 'todo' ? 'Aufgabe anlegen' : 'Notiz anlegen'}
        </button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Lädt…</p> : (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Offene Aufgaben */}
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm">
              {(data?.todos?.length ?? 0) > 0 ? `🔴 Offene Aufgaben (${data.todos.length})` : '✅ Keine offenen Aufgaben'}
            </h3>
            {(data?.todos ?? []).map(t => (
              <div key={t.id} className="flex items-start justify-between gap-2 text-sm border rounded-md px-3 py-2">
                <div className="min-w-0">
                  <div className={t.prioritaet === 'wichtig' ? 'font-semibold text-amber-600' : ''}>
                    {t.prioritaet === 'wichtig' ? '❗ ' : ''}{t.text}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t.erstellt_von_name} · {zeit(t.created_at)} · {t.quelle === 'app' ? 'aus der App' : 'aus dem CRM'}
                    {t.sichtbar_fuer === 'leitung' ? ' · 🔒 nur Leitung'
                      : t.sichtbar_fuer === 'auswahl' ? ` · 🔒 ${(t.empfaenger || []).length} Person(en)` : ''}
                  </div>
                </div>
                <button onClick={() => remove(t)} className="text-xs text-destructive hover:underline shrink-0">Löschen</button>
              </div>
            ))}
            {(data?.todos?.length ?? 0) === 0 && <p className="text-xs text-muted-foreground">Die Schichten haben alles abgearbeitet.</p>}
          </div>

          {/* Notizen */}
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm">📋 Notizen der letzten 7 Tage</h3>
            {(data?.infos ?? []).map(i => (
              <div key={i.id} className="flex items-start justify-between gap-2 text-sm border rounded-md px-3 py-2">
                <div className="min-w-0">
                  <div className={i.prioritaet === 'wichtig' ? 'text-amber-600' : ''}>
                    {i.prioritaet === 'wichtig' ? '❗ ' : ''}{i.text}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {i.erstellt_von_name} · {zeit(i.created_at)} · {i.quelle === 'app' ? 'aus der App' : 'aus dem CRM'}
                    {i.sichtbar_fuer === 'leitung' ? ' · 🔒 nur Leitung'
                      : i.sichtbar_fuer === 'auswahl' ? ` · 🔒 ${(i.empfaenger || []).length} Person(en)` : ''}
                  </div>
                </div>
                <button onClick={() => remove(i)} className="text-xs text-destructive hover:underline shrink-0">Löschen</button>
              </div>
            ))}
            {(data?.infos?.length ?? 0) === 0 && <p className="text-xs text-muted-foreground">Noch nichts hinterlassen.</p>}
          </div>
        </div>
      )}

      {/* Erledigt-Verlauf */}
      {(data?.zuletzt_erledigt?.length ?? 0) > 0 && (
        <div className="border rounded-lg p-4 space-y-1.5">
          <h3 className="font-semibold text-sm">✓ Zuletzt erledigt</h3>
          {data.zuletzt_erledigt.map(e => (
            <div key={e.id} className="text-sm text-muted-foreground">
              {e.text} — <span className="text-foreground">{e.erledigt_von_name}</span>, {zeit(e.erledigt_am)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Events (Sommerfest, Gymfights …): anlegen + Gästeliste ──
function EventsManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ titel: '', emoji: '🎉', datum: '', zeit: '', ort: '', beschreibung: '', gaeste_pro_mitglied: 5 });
  const [saving, setSaving] = useState(false);
  const [openGuests, setOpenGuests] = useState(null);   // event_id
  const [guests, setGuests] = useState(null);           // {zusagen, gaeste}
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/admin/events`, { headers: getHeaders() });
      if (res.ok) setEvents((await res.json()).events || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    // Datum tolerant: "20.09.2026" oder "2026-09-20" → ISO
    let datum = form.datum.trim();
    const de = datum.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (de) datum = `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
    if (!form.titel.trim()) { toast.error('Bitte einen Titel eingeben'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) {
      toast.error('Datum bitte als TT.MM.JJJJ oder JJJJ-MM-TT (z. B. 20.09.2026)'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/public/admin/events`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, datum, gaeste_pro_mitglied: parseInt(form.gaeste_pro_mitglied, 10) || 5 }),
      });
      if (res.ok) {
        toast.success('Event angelegt — ab sofort in der App sichtbar');
        setForm({ titel: '', emoji: '🎉', datum: '', zeit: '', ort: '', beschreibung: '', gaeste_pro_mitglied: 5 });
        load();
      } else toast.error((await res.json())?.detail || 'Fehler beim Anlegen');
    } catch { toast.error('Netzwerkfehler'); }
    setSaving(false);
  };

  const toggleActive = async (ev) => {
    try {
      const res = await fetch(`${API}/api/public/admin/events/${ev.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !ev.active }),
      });
      if (res.ok) load();
    } catch { toast.error('Fehler'); }
  };

  const showGuests = async (ev) => {
    if (openGuests === ev.id) { setOpenGuests(null); setGuests(null); return; }
    setOpenGuests(ev.id); setGuests(null);
    try {
      const res = await fetch(`${API}/api/public/admin/events/${ev.id}/gaeste`, { headers: getHeaders() });
      if (res.ok) setGuests(await res.json());
    } catch { toast.error('Fehler beim Laden'); }
  };

  const guestToLead = async (g) => {
    try {
      const res = await fetch(`${API}/api/public/admin/events/gaeste/${g.id}/lead`, {
        method: 'POST', headers: getHeaders(),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d?.detail || 'Fehler'); return; }
      toast.success(d.message);
      // Gästeliste neu laden, damit "Lead ✓" erscheint
      const r2 = await fetch(`${API}/api/public/admin/events/${g.event_id}/gaeste`, { headers: getHeaders() });
      if (r2.ok) setGuests(await r2.json());
    } catch { toast.error('Netzwerkfehler'); }
  };

  return (
    <div className="space-y-4" data-testid="events-manager">
      {/* Anlegen */}
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-sm">Neues Event</h3>
        <div className="flex items-start gap-2 flex-wrap">
          <input value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm bg-background w-14 text-center" placeholder="🎉" />
          <input value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))}
            className="border rounded-md px-3 py-1.5 text-sm bg-background w-52" placeholder="Sommerfest / Gymfights" />
          <input value={form.datum} onChange={e => setForm(f => ({ ...f, datum: e.target.value }))}
            className="border rounded-md px-3 py-1.5 text-sm bg-background w-32" placeholder="20.09.2026" />
          <input value={form.zeit} onChange={e => setForm(f => ({ ...f, zeit: e.target.value }))}
            className="border rounded-md px-3 py-1.5 text-sm bg-background w-24" placeholder="18:00" />
          <input value={form.ort} onChange={e => setForm(f => ({ ...f, ort: e.target.value }))}
            className="border rounded-md px-3 py-1.5 text-sm bg-background w-40" placeholder="Ort (optional)" />
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <input type="number" min="1" max="20" value={form.gaeste_pro_mitglied}
              onChange={e => setForm(f => ({ ...f, gaeste_pro_mitglied: e.target.value }))}
              className="border rounded-md px-2 py-1.5 text-sm bg-background w-16" />
            Gäste/Mitglied
          </div>
        </div>
        <textarea value={form.beschreibung} onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))}
          className="border rounded-md px-3 py-1.5 text-sm bg-background w-full" rows={2}
          placeholder="Kurzbeschreibung für die Einladung (optional)" />
        <button onClick={create} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Event anlegen
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Lädt…</p>
      ) : events.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-sm text-muted-foreground">Noch keine Events.</div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <span className="text-lg mr-1">{ev.emoji}</span>
                  <span className={`font-semibold ${ev.active ? '' : 'opacity-50 line-through'}`}>{ev.titel}</span>
                  <span className="text-muted-foreground"> · {ev.datum}{ev.zeit ? ` ${ev.zeit}` : ''}{ev.ort ? ` · ${ev.ort}` : ''}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    ✓ {ev.zusagen} Mitglieder · 🎟️ {ev.gaeste_personen} Gäste ({ev.gaeste_anmeldungen} Anmeldungen)
                  </span>
                  <button onClick={() => showGuests(ev)} className="text-xs text-primary hover:underline">
                    {openGuests === ev.id ? 'Zuklappen' : 'Gästeliste'}
                  </button>
                  <button onClick={() => toggleActive(ev)} className="text-xs text-muted-foreground hover:underline">
                    {ev.active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                </div>
              </div>

              {openGuests === ev.id && (
                !guests ? <p className="text-xs text-muted-foreground">Lädt…</p> : (
                  <div className="grid sm:grid-cols-2 gap-3 pt-2">
                    <div>
                      <p className="text-xs font-semibold mb-1">Zusagen Mitglieder ({guests.zusagen.length})</p>
                      <div className="space-y-1 max-h-48 overflow-auto">
                        {guests.zusagen.length === 0 ? <p className="text-xs text-muted-foreground">Noch keine.</p>
                          : guests.zusagen.map(z => (
                            <div key={z.id} className="text-sm border rounded-md px-2 py-1">{z.member_name}</div>
                          ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold mb-1">Gäste ({guests.gaeste.length})</p>
                      <div className="space-y-1 max-h-48 overflow-auto">
                        {guests.gaeste.length === 0 ? <p className="text-xs text-muted-foreground">Noch keine.</p>
                          : guests.gaeste.map(g => (
                            <div key={g.id} className="flex items-center justify-between text-sm border rounded-md px-2 py-1 gap-2">
                              <span className="min-w-0">
                                {g.gast_name} <span className="text-muted-foreground">· {g.phone} · {g.personen}P · via {g.eingeladen_von_name}</span>
                              </span>
                              {g.lead_id ? (
                                <a href={`/leads/${g.lead_id}`} className="text-xs text-emerald-600 hover:underline shrink-0">Lead ✓</a>
                              ) : (
                                <button onClick={() => guestToLead(g)} className="text-xs text-primary hover:underline shrink-0">Als Lead übernehmen</button>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Aktive Events erscheinen automatisch in der App: Mitglieder sagen zu und laden Gäste per
        WhatsApp/QR ein (Limit pro Mitglied greift serverseitig). Gäste mit Nummer kannst du hier
        direkt als Lead übernehmen.
      </p>
    </div>
  );
}

// ── App-Zugänge: Prüf-Anfragen aus der Bestandskunden-Migration ──
// Mitglied hat "Zugang aktivieren" versucht, Daten passten nicht → hier
// vergleichen, ggf. den richtigen Lead wählen und freigeben (übernimmt die
// eingegebene Handynummer; E-Mail nur, wenn am Lead noch keine steht).
function OnboardingManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('offen');
  const [picks, setPicks] = useState({});      // request_id -> gewählter Lead
  const [searches, setSearches] = useState({}); // request_id -> {q, results}
  const [busyId, setBusyId] = useState(null);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/admin/onboarding-requests?status=${filter}`, { headers: getHeaders() });
      if (res.ok) setItems((await res.json()).requests || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const search = async (reqId, q) => {
    setSearches(s => ({ ...s, [reqId]: { q, results: s[reqId]?.results || [] } }));
    if (q.trim().length < 2) return;
    try {
      const res = await fetch(`${API}/api/leads?search=${encodeURIComponent(q.trim())}&limit=5`, { headers: getHeaders() });
      if (res.ok) {
        const leads = (await res.json()).leads || [];
        setSearches(s => ({ ...s, [reqId]: { q, results: leads } }));
      }
    } catch { /* Ergebnis bleibt leer */ }
  };

  const decide = async (r, status) => {
    const lead = picks[r.id] || r.best_match;
    if (status === 'freigegeben' && !lead) { toast.error('Bitte zuerst den passenden Datensatz wählen'); return; }
    setBusyId(r.id);
    try {
      const res = await fetch(`${API}/api/public/admin/onboarding-requests/${r.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, lead_id: lead?.id || null }),
      });
      if (res.ok) {
        toast.success(status === 'freigegeben'
          ? `Freigegeben — ${r.vorname} kann den Zugang jetzt aktivieren`
          : 'Anfrage abgelehnt');
        load();
      } else toast.error((await res.json())?.detail || 'Fehler');
    } catch { toast.error('Netzwerkfehler'); }
    setBusyId(null);
  };

  return (
    <div className="space-y-4" data-testid="onboarding-manager">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-muted/50 rounded-lg p-1 w-fit">
          {[['offen', 'Offen'], ['freigegeben', 'Freigegeben'], ['abgelehnt', 'Abgelehnt'], ['alle', 'Alle']].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={load} className="p-2 rounded-md hover:bg-muted" title="Aktualisieren">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Lädt…</p>
      ) : items.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-sm text-muted-foreground">
          Keine {filter === 'alle' ? '' : `${filter}en `}Zugangs-Anfragen. Anfragen entstehen, wenn ein
          Bestandsmitglied „Zugang aktivieren" nutzt und die Daten nicht zum CRM passen.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(r => {
            const pick = picks[r.id] || r.best_match;
            const s = searches[r.id] || { q: '', results: [] };
            return (
              <div key={r.id} className="border rounded-lg p-4 space-y-2" data-testid="onboarding-request">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm">
                    <span className="font-semibold">{r.vorname} {r.nachname}</span>
                    <span className="text-muted-foreground"> · {r.phone}{r.email ? ` · ${r.email}` : ''}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    r.status === 'offen' ? 'bg-amber-500/15 text-amber-600'
                    : r.status === 'freigegeben' ? 'bg-emerald-500/15 text-emerald-600'
                    : 'bg-muted text-muted-foreground'
                  }`}>{r.status}</span>
                </div>

                {r.status === 'offen' && (
                  <>
                    <div className="text-xs text-muted-foreground">
                      {pick ? (
                        <>Zuordnung: <span className="font-medium text-foreground">{pick.name}</span>
                          {' '}({pick.phone || 'keine Nummer'}{pick.email ? ` · ${pick.email}` : ''})
                          {r.best_match && pick.id === r.best_match.id ? ' — bester Treffer' : ''}</>
                      ) : 'Kein passender Datensatz gefunden — bitte unten suchen.'}
                    </div>
                    <div className="relative w-64">
                      <input
                        value={s.q}
                        onChange={e => search(r.id, e.target.value)}
                        placeholder="Anderen Datensatz suchen…"
                        className="border rounded-md px-3 py-1.5 text-sm bg-background w-full"
                      />
                      {s.results.length > 0 && s.q.trim().length >= 2 && (
                        <div className="absolute z-10 mt-1 w-full bg-background border rounded-md shadow-md max-h-40 overflow-auto">
                          {s.results.map(l => (
                            <button key={l.id}
                              onClick={() => { setPicks(p => ({ ...p, [r.id]: l })); setSearches(se => ({ ...se, [r.id]: { q: '', results: [] } })); }}
                              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-muted">
                              {l.name} <span className="text-muted-foreground">({l.phone || '–'})</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <button onClick={() => decide(r, 'freigegeben')} disabled={busyId === r.id || !pick}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                        <Check className="w-4 h-4" /> Freigeben & Nummer übernehmen
                      </button>
                      <button onClick={() => decide(r, 'abgelehnt')} disabled={busyId === r.id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium border hover:bg-muted disabled:opacity-50">
                        <X className="w-4 h-4" /> Ablehnen
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Freigeben übernimmt die vom Mitglied eingegebene Handynummer in den gewählten Datensatz
        (E-Mail nur, wenn dort noch keine steht). Danach klappt „Zugang aktivieren" in der App sofort.
      </p>
    </div>
  );
}

// ── Familien-Hinweise: "Meine Familie ist schon bei P1" ──
// Verknüpfen passiert im Lead unter Familie — hier nur die Aufgabenliste.
function FamilyHintManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/family/admin/link-requests?status=offen`, { headers: getHeaders() });
      if (res.ok) setItems((await res.json()).requests || []);
    } catch { /* leer lassen */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (r, status) => {
    setBusyId(r.id);
    try {
      const res = await fetch(`${API}/api/public/family/admin/link-requests/${r.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(status === 'erledigt' ? 'Erledigt — das Mitglied wird benachrichtigt' : 'Hinweis abgelehnt');
        load();
      } else toast.error((await res.json())?.detail || 'Fehler');
    } catch { toast.error('Netzwerkfehler'); }
    setBusyId(null);
  };

  if (loading || items.length === 0) return null;
  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="family-hint-manager">
      <div>
        <h3 className="font-semibold text-sm">👨‍👩‍👧 Familien-Hinweise</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Mitglieder haben angegeben, dass ihre Familie schon bei P1 ist. Zum Verknüpfen den Lead
          öffnen (Tab Familie), danach hier auf Erledigt — das Mitglied bekommt einen Push.
        </p>
      </div>
      <div className="space-y-1.5">
        {items.map(r => (
          <div key={r.id} className="flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-2">
            <div className="min-w-0">
              <a href={`/leads/${r.account_id}`} className="font-medium hover:underline">{r.account_name || r.account_id}</a>
              <span className="text-muted-foreground"> · „{r.text}"</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => decide(r, 'erledigt')} disabled={busyId === r.id}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                <Check className="w-3.5 h-3.5" /> Erledigt
              </button>
              <button onClick={() => decide(r, 'abgelehnt')} disabled={busyId === r.id}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border hover:bg-muted disabled:opacity-50">
                <X className="w-3.5 h-3.5" /> Ablehnen
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Check-Up-Anfragen aus der App (Familie): bestätigen/ablehnen ──
function CheckupManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('offen');
  const [termine, setTermine] = useState({});   // request_id -> Termin-Eingabe
  const [busyId, setBusyId] = useState(null);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/family/admin/checkup-requests?status=${filter}`, { headers: getHeaders() });
      if (res.ok) setItems((await res.json()).requests || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (r, status) => {
    const termin = (termine[r.id] || '').trim();
    if (status === 'bestaetigt' && !termin) { toast.error('Bitte zuerst einen Termin eintragen'); return; }
    setBusyId(r.id);
    try {
      const res = await fetch(`${API}/api/public/family/admin/checkup-requests/${r.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, termin: termin || null }),
      });
      if (res.ok) {
        toast.success(status === 'bestaetigt' ? `Termin bestätigt — die Familie wird benachrichtigt` : 'Anfrage abgelehnt');
        load();
      } else {
        toast.error((await res.json())?.detail || 'Fehler beim Speichern');
      }
    } catch { toast.error('Netzwerkfehler'); }
    setBusyId(null);
  };

  const ZEITFENSTER_LABEL = { vormittag: 'Vormittag', nachmittag: 'Nachmittag', abend: 'Abend' };
  // 2026-10-12 -> 12.10.2026 (wie die Karte „Check-Up“); alles andere bleibt, wie es kommt
  const tagDE = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || '') ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : (iso || ''));

  return (
    <div className="space-y-4" data-testid="checkup-manager">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-muted/50 rounded-lg p-1 w-fit">
          {[['offen', 'Offen'], ['bestaetigt', 'Bestätigt'], ['erledigt', 'Erledigt'], ['abgelehnt', 'Abgelehnt'], ['alle', 'Alle']].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={load} className="p-2 rounded-md hover:bg-muted" title="Aktualisieren">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Lädt…</p>
      ) : items.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-sm text-muted-foreground">
          Keine {filter === 'alle' ? '' : `${filter}en `}Check-Up-Anfragen.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <div key={r.id} className="border rounded-lg p-4 space-y-2" data-testid="checkup-request">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="font-semibold">{r.profil_name}</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    Wunsch: {tagDE(r.wunschdatum)} · {ZEITFENSTER_LABEL[r.zeitfenster] || r.zeitfenster}
                  </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  r.status === 'offen' ? 'bg-amber-500/15 text-amber-600'
                  : r.status === 'bestaetigt' ? 'bg-emerald-500/15 text-emerald-600'
                  : 'bg-muted text-muted-foreground'
                }`}>
                  {r.status === 'bestaetigt' ? `Bestätigt: ${r.termin}` : r.status === 'erledigt' ? `Erledigt${r.checkup_tag ? `: Check-Up am ${tagDE(r.checkup_tag)}` : ''}` : r.status}
                </span>
              </div>
              {r.notiz && <p className="text-sm text-muted-foreground">Notiz: {r.notiz}</p>}
              {r.status === 'offen' && (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <input
                    value={termine[r.id] || ''}
                    onChange={e => setTermine(t => ({ ...t, [r.id]: e.target.value }))}
                    placeholder="Termin, z. B. 12.08.2026 17:30"
                    className="border rounded-md px-3 py-1.5 text-sm bg-background w-56"
                  />
                  <button onClick={() => decide(r, 'bestaetigt')} disabled={busyId === r.id}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                    <Check className="w-4 h-4" /> Bestätigen
                  </button>
                  <button onClick={() => decide(r, 'abgelehnt')} disabled={busyId === r.id}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium border hover:bg-muted disabled:opacity-50">
                    <X className="w-4 h-4" /> Ablehnen
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Anfragen kommen aus der App (Familie → Check-Up). Bei Bestätigung/Ablehnung wird das Elternkonto
        per Push und E-Mail benachrichtigt. Nach dem durchgeführten Check-Up bitte in der Karte „Check-Up“ am Mitglied
        (in der Akte oder unter Ernährung › Mitglied) den Check-Up eintragen — das steuert „fällig" und die Erinnerung
        und setzt die bestätigte Anfrage auf „erledigt“, sodass die Familie den nächsten Termin anfragen kann. Ein
        eingetragener Check-Up schließt jede bis zu seinem Tag bestätigte Anfrage dieses Profils. Ausnahme: Wird ein
        früherer Tag nachgetragen, bleibt eine Anfrage stehen, deren Termin noch bevorsteht — dafür den Termin mit
        Datum eintragen (TT.MM.JJJJ, z. B. 12.08.2026 17:30).
      </p>
    </div>
  );
}

// ── News-Feed für den App-Home-Screen ──────────────────────────
const NEWS_EMOJIS = ['📣', '🥊', '🏆', '☀️', '🎉', '🔥', '📅', '💪', '🎁', '⚡'];

function NewsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emoji, setEmoji] = useState('📣');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/admin/news`, { headers: getHeaders() });
      if (res.ok) setItems((await res.json()).news || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!title.trim() || !text.trim()) { toast.error('Titel und Text sind erforderlich'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/public/admin/news`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), text: text.trim(), emoji }),
      });
      if (res.ok) { toast.success('News veröffentlicht'); setTitle(''); setText(''); setEmoji('📣'); load(); }
      else toast.error('Fehler beim Speichern');
    } catch { toast.error('Netzwerkfehler'); }
    setSaving(false);
  };

  const toggleActive = async (n) => {
    try {
      const res = await fetch(`${API}/api/public/admin/news/${n.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !n.active }),
      });
      if (res.ok) load();
    } catch { toast.error('Fehler'); }
  };

  const remove = async (n) => {
    if (!window.confirm(`News "${n.title}" löschen?`)) return;
    try {
      const res = await fetch(`${API}/api/public/admin/news/${n.id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { toast.success('Gelöscht'); load(); }
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="max-w-xl space-y-4">
      <div className="bg-card border border-border/60 rounded-lg p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {NEWS_EMOJIS.map(e => (
            <button key={e} onClick={() => setEmoji(e)}
              className={`w-9 h-9 rounded-md text-lg ${emoji === e ? 'bg-primary/15 border border-primary/40' : 'bg-muted/40'}`}>{e}</button>
          ))}
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
          placeholder="Titel, z.B. Neuer Kurs: Ladies Boxing"
          className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm" />
        <textarea value={text} onChange={e => setText(e.target.value)} maxLength={200} rows={2}
          placeholder="Kurztext…"
          className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm resize-none" />
        <button onClick={create} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
          <Plus className="w-4 h-4" /> {saving ? 'Speichert…' : 'News veröffentlichen'}
        </button>
      </div>

      {loading ? <div className="h-24 bg-muted animate-pulse rounded-lg" /> : (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Noch keine News.</div>}
          {items.map(n => (
            <div key={n.id} className="flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0">
              <span className="text-2xl">{n.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${!n.active && 'text-muted-foreground line-through'}`}>{n.title}</p>
                <p className="text-xs text-muted-foreground truncate">{n.text}</p>
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0">{n.date_label}</span>
              <button onClick={() => toggleActive(n)} title={n.active ? 'Ausblenden' : 'Einblenden'}
                className={`p-2 rounded-md ${n.active ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                {n.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(n)} className="p-2 rounded-md bg-red-500/10 text-red-500 hover:bg-red-500/20">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Support-Tickets aus der App ────────────────────────────────
const TICKET_STATUS = { offen: { label: 'Offen', color: 'text-amber-600' }, in_bearbeitung: { label: 'In Bearbeitung', color: 'text-blue-600' }, geloest: { label: 'Gelöst', color: 'text-green-600' } };
const TICKET_CAT = { allgemein: 'Allgemein', buchung: 'Buchung', zahlung: 'Zahlung', mitgliedschaft: 'Mitgliedschaft', technik: 'Technik', sonstiges: 'Sonstiges' };

function SupportManager() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('offen');
  const [loading, setLoading] = useState(true);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'alle' ? '' : `?status=${filter}`;
      const res = await fetch(`${API}/api/public/admin/support${q}`, { headers: getHeaders() });
      if (res.ok) setTickets((await res.json()).tickets || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/public/admin/support/${id}?status=${status}`, { method: 'PATCH', headers: getHeaders() });
      if (res.ok) { toast.success('Aktualisiert'); load(); } else toast.error('Fehler');
    } catch { toast.error('Netzwerkfehler'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {['offen', 'in_bearbeitung', 'geloest', 'alle'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium ${filter === f ? 'bg-background shadow-sm text-foreground border border-border' : 'text-muted-foreground'}`}>
            {f === 'alle' ? 'Alle' : (TICKET_STATUS[f]?.label || f)}
          </button>
        ))}
        <button onClick={load} className="ml-auto p-1.5 text-muted-foreground hover:text-foreground"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {loading ? <div className="h-32 bg-muted animate-pulse rounded-lg" /> : tickets.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-lg p-8 text-center text-sm text-muted-foreground">Keine Tickets in dieser Ansicht.</div>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => (
            <div key={t.id} className="bg-card border border-border/60 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{t.subject}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{TICKET_CAT[t.category] || t.category}</span>
                    <span className={`text-[10px] font-semibold ${TICKET_STATUS[t.status]?.color || ''}`}>{TICKET_STATUS[t.status]?.label || t.status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t.member_name} · {new Date(t.created_at).toLocaleString('de-DE')}</p>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{t.message}</p>
                </div>
              </div>
              <div className="flex gap-1.5 mt-3">
                {t.status !== 'in_bearbeitung' && <button onClick={() => setStatus(t.id, 'in_bearbeitung')} className="px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-600 text-xs font-medium">In Bearbeitung</button>}
                {t.status !== 'geloest' && <button onClick={() => setStatus(t.id, 'geloest')} className="px-2.5 py-1 rounded-md bg-green-500/10 text-green-600 text-xs font-medium">Gelöst</button>}
                {t.status !== 'offen' && <button onClick={() => setStatus(t.id, 'offen')} className="px-2.5 py-1 rounded-md bg-muted text-muted-foreground text-xs font-medium">Wieder öffnen</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Push-Broadcast an alle App-Geräte ──────────────────────────
function PushBroadcast() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const loadLog = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/push-log`, { headers: getHeaders() });
      if (res.ok) setLog((await res.json()).log || []);
    } catch { /* still */ }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Titel und Text sind erforderlich'); return; }
    if (!window.confirm(`Push an ALLE App-Geräte senden?\n\n${title}\n${body}`)) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/notifications/push-broadcast`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), body: body.trim() }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || 'Gesendet');
        setTitle(''); setBody(''); loadLog();
      } else toast.error(d.detail || 'Fehler beim Senden');
    } catch { toast.error('Netzwerkfehler'); }
    setSending(false);
  };

  return (
    <div className="max-w-xl space-y-4">
      <div className="bg-card border border-border/60 rounded-lg p-4 space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Titel</label>
          <input value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
            placeholder="z.B. Heute freies Training!"
            className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nachricht</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={200} rows={3}
            placeholder="Kurze Nachricht an alle Mitglieder…"
            className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm resize-none" />
          <p className="text-[10px] text-muted-foreground mt-1">{body.length}/200 Zeichen</p>
        </div>
        <button onClick={send} disabled={sending}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
          <Send className="w-4 h-4" /> {sending ? 'Wird gesendet…' : 'An alle senden'}
        </button>
        <p className="text-[11px] text-muted-foreground">
          Erreicht jedes Gerät, das die App installiert und Benachrichtigungen erlaubt hat.
        </p>
      </div>

      {log.length > 0 && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b bg-muted/30 text-xs font-medium text-muted-foreground">Zuletzt gesendet</div>
          {log.map(l => (
            <div key={l.id} className="px-4 py-2.5 border-b border-border/30 last:border-0">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="text-sm font-medium">{l.title}</p>
                  <p className="text-xs text-muted-foreground">{l.body}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold">{l.sent_to} Geräte</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(l.created_at).toLocaleString('de-DE')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Prämien-Einlösungen verwalten ──────────────────────────────
function RewardsManager() {
  const [redemptions, setRedemptions] = useState([]);
  const [filter, setFilter] = useState('offen');
  const [loading, setLoading] = useState(true);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'alle' ? '' : `?status=${filter}`;
      const res = await fetch(`${API}/api/public/admin/redemptions${q}`, { headers: getHeaders() });
      if (res.ok) setRedemptions((await res.json()).redemptions || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const settle = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/public/admin/redemptions/${id}?status=${encodeURIComponent(status)}`, {
        method: 'PATCH', headers: getHeaders(),
      });
      if (res.ok) { toast.success(status === 'eingelöst' ? 'Als eingelöst markiert' : 'Storniert – Punkte zurück'); load(); }
      else toast.error('Fehler');
    } catch { toast.error('Netzwerkfehler'); }
  };

  const STATUS_COLOR = { offen: 'text-amber-600', 'eingelöst': 'text-green-600', storniert: 'text-muted-foreground' };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {['offen', 'eingelöst', 'storniert', 'alle'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize ${filter === f ? 'bg-background shadow-sm text-foreground border border-border' : 'text-muted-foreground'}`}>
            {f}
          </button>
        ))}
        <button onClick={load} className="ml-auto p-1.5 text-muted-foreground hover:text-foreground"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {loading ? (
        <div className="h-32 bg-muted animate-pulse rounded-lg" />
      ) : redemptions.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-lg p-8 text-center text-sm text-muted-foreground">
          Keine Einlösungen in dieser Ansicht.
        </div>
      ) : (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          {redemptions.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0">
              <span className="text-2xl">{r.reward_emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{r.reward_title}</p>
                <p className="text-xs text-muted-foreground">
                  {r.member_name} · {r.points} P · {new Date(r.created_at).toLocaleDateString('de-DE')}
                </p>
              </div>
              <div className="font-mono text-sm font-bold bg-muted px-2.5 py-1 rounded">{r.code}</div>
              {r.status === 'offen' ? (
                <div className="flex gap-1.5">
                  <button onClick={() => settle(r.id, 'eingelöst')} title="Als eingelöst markieren"
                    className="p-2 rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20"><Check className="w-4 h-4" /></button>
                  <button onClick={() => settle(r.id, 'storniert')} title="Stornieren (Punkte zurück)"
                    className="p-2 rounded-md bg-red-500/10 text-red-500 hover:bg-red-500/20"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <span className={`text-xs font-semibold capitalize ${STATUS_COLOR[r.status] || ''}`}>{r.status}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingsOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonday, setCurrentMonday] = useState(getMonday(new Date()));
  const weekDate = formatWeekDate(currentMonday);

  const getHeaders = () => tokenStore.getAuthHeaders();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/admin/bookings-overview?week_date=${weekDate}`, { headers: getHeaders() });
      if (res.ok) setData(await res.json());
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, [weekDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cancelBooking = async (bookingId) => {
    try {
      const res = await fetch(`${API}/api/kursplan/book/${bookingId}`, {
        method: 'DELETE',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      });
      if (res.ok) { toast.success('Buchung storniert'); fetchData(); }
      else toast.error('Fehler beim Stornieren');
    } catch { toast.error('Netzwerkfehler'); }
  };

  const prevWeek = () => { const d = new Date(currentMonday); d.setDate(d.getDate() - 7); setCurrentMonday(d); };
  const nextWeek = () => { const d = new Date(currentMonday); d.setDate(d.getDate() + 7); setCurrentMonday(d); };

  const stats = data?.course_stats ? Object.values(data.course_stats) : [];
  const sortedStats = useMemo(() =>
    [...stats].sort((a, b) => {
      const dayOrder = DAYS.indexOf(a.day) - DAYS.indexOf(b.day);
      return dayOrder !== 0 ? dayOrder : a.start.localeCompare(b.start);
    }), [stats]
  );

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="bookings-overview">
      {/* Week Nav + Stats */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-3">
          <div className="bg-card border border-border rounded-lg px-4 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-bold text-primary">{data?.total_bookings || 0}</div>
            <div className="text-xs text-muted-foreground">Gesamt</div>
          </div>
          <div className="bg-card border border-border rounded-lg px-4 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-bold text-emerald-600 flex items-center justify-center gap-1">
              <Smartphone className="w-4 h-4" />{data?.app_bookings_count || 0}
            </div>
            <div className="text-xs text-muted-foreground">App</div>
          </div>
          <div className="bg-card border border-border rounded-lg px-4 py-3 text-center min-w-[100px]">
            <div className="text-2xl font-bold text-blue-600 flex items-center justify-center gap-1">
              <Monitor className="w-4 h-4" />{data?.crm_bookings_count || 0}
            </div>
            <div className="text-xs text-muted-foreground">CRM</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-1.5 hover:bg-accent rounded-lg transition-colors" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <div className="flex items-center bg-card border border-border rounded-lg">
            <button onClick={prevWeek} className="p-1.5 hover:bg-accent rounded-l-lg"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-3 py-1.5 text-sm font-medium min-w-[140px] text-center">{formatDateRange(currentMonday)}</span>
            <button onClick={nextWeek} className="p-1.5 hover:bg-accent rounded-r-lg"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Course Stats Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="course-stats-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-2.5 font-semibold">Kurs</th>
                <th className="text-left px-3 py-2.5 font-semibold">Tag</th>
                <th className="text-left px-3 py-2.5 font-semibold">Zeit</th>
                <th className="text-center px-3 py-2.5 font-semibold">App</th>
                <th className="text-center px-3 py-2.5 font-semibold">CRM</th>
                <th className="text-center px-3 py-2.5 font-semibold">Gesamt</th>
                <th className="text-center px-3 py-2.5 font-semibold">Frei</th>
                <th className="px-3 py-2.5 font-semibold">Auslastung</th>
              </tr>
            </thead>
            <tbody>
              {sortedStats.map((s) => {
                const pct = Math.round((s.total_booked / 16) * 100);
                return (
                  <tr key={`course-${s.course_name}-${s.day}-${s.start}`} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2 font-medium">{s.course_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{s.day}</td>
                    <td className="px-3 py-2 text-muted-foreground">{s.start} - {s.end}</td>
                    <td className="px-3 py-2 text-center">
                      {s.app_booked > 0 && <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><Smartphone className="w-3 h-3" />{s.app_booked}</span>}
                      {s.app_booked === 0 && <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {s.crm_booked > 0 && <span className="inline-flex items-center gap-1 text-blue-600 font-medium"><Monitor className="w-3 h-3" />{s.crm_booked}</span>}
                      {s.crm_booked === 0 && <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="px-3 py-2 text-center font-semibold">{s.total_booked}</td>
                    <td className="px-3 py-2 text-center">{s.available}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-border rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-8">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Bookings List */}
      {data?.all_bookings?.length > 0 && (
        <div className="bg-card border border-border rounded-lg" data-testid="recent-bookings">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold">Letzte Buchungen</h3>
          </div>
          <div className="divide-y divide-border/50 max-h-[300px] overflow-y-auto">
            {data.all_bookings.slice(0, 20).map(b => (
              <div key={b.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/20 transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center ${b.source === 'app' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-blue-500/10 text-blue-600'}`}>
                    {b.source === 'app' ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                  </span>
                  <div>
                    <p className="text-sm font-medium">{b.user_name}</p>
                    <p className="text-xs text-muted-foreground">{b.course_name} &middot; {b.day} {b.start}</p>
                  </div>
                </div>
                <button
                  onClick={() => cancelBooking(b.id)}
                  className="text-xs text-destructive hover:underline"
                  data-testid={`cancel-${b.id}`}
                >
                  Stornieren
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <StammplatzManager courseStats={data?.course_stats || {}} onChanged={fetchData} />
    </div>
  );
}

// ── Stammplätze: Auto-Buchung mit Opt-out (Octiv-Modell) ───────
// Feste Kursplätze (v. a. Kinder) — der Scheduler bucht jede Woche automatisch,
// Familien müssen nur stornieren. Vergabe nur hier am Empfang.
function StammplatzManager({ courseStats, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selMember, setSelMember] = useState(null);
  const [selCourse, setSelCourse] = useState('');
  const [busy, setBusy] = useState(false);
  const getHeaders = () => tokenStore.getAuthHeaders();

  const load = useCallback(async () => {
    try {
      const [res, reqRes] = await Promise.all([
        fetch(`${API}/api/public/admin/standing-bookings`, { headers: getHeaders() }),
        fetch(`${API}/api/public/admin/standing-requests?status=offen`, { headers: getHeaders() }),
      ]);
      if (res.ok) setEntries((await res.json()).standing_bookings || []);
      if (reqRes.ok) setRequests((await reqRes.json()).requests || []);
    } catch { /* Liste bleibt leer */ }
    setLoading(false);
  }, []);

  const decideRequest = async (r, status) => {
    try {
      const res = await fetch(`${API}/api/public/admin/standing-requests/${r.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(status === 'bestaetigt'
          ? `Stammplatz für ${r.member_name} bestätigt — Familie wird benachrichtigt`
          : 'Anfrage abgelehnt');
        load(); onChanged?.();
      } else toast.error((await res.json())?.detail || 'Fehler');
    } catch { toast.error('Netzwerkfehler'); }
  };

  useEffect(() => { load(); }, [load]);

  const doSearch = async (q) => {
    setSearch(q); setSelMember(null);
    if (q.trim().length < 2) { setResults([]); return; }
    try {
      const res = await fetch(`${API}/api/leads?search=${encodeURIComponent(q.trim())}&limit=6`, { headers: getHeaders() });
      if (res.ok) setResults((await res.json()).leads || []);
    } catch { setResults([]); }
  };

  const create = async () => {
    if (!selMember || !selCourse) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/public/admin/standing-bookings`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: selMember.id, course_id: selCourse }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`Stammplatz angelegt — ${d.gebucht_wochen} Woche(n) direkt eingebucht`);
        setSearch(''); setResults([]); setSelMember(null); setSelCourse('');
        load(); onChanged?.();
      } else toast.error(d?.detail || 'Fehler beim Anlegen');
    } catch { toast.error('Netzwerkfehler'); }
    setBusy(false);
  };

  const toggle = async (e) => {
    try {
      const res = await fetch(`${API}/api/public/admin/standing-bookings/${e.id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !e.active }),
      });
      if (res.ok) { load(); onChanged?.(); }
    } catch { toast.error('Fehler'); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Stammplatz von ${e.member_name} für ${e.course_name} beenden?`)) return;
    try {
      const res = await fetch(`${API}/api/public/admin/standing-bookings/${e.id}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (res.ok) { toast.success('Stammplatz beendet'); load(); }
    } catch { toast.error('Fehler'); }
  };

  const courseList = Object.entries(courseStats)
    .sort(([, a], [, b]) => (DAYS.indexOf(a.day) - DAYS.indexOf(b.day)) || a.start.localeCompare(b.start));

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="stammplatz-manager">
      <div>
        <h3 className="font-semibold text-sm">📌 Stammplätze (Auto-Buchung)</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Wird jede Woche automatisch eingebucht (laufende + nächste Woche) — die Familie muss nur
          stornieren, wenn das Kind nicht kommt. Eine Absage wird nie von der Automatik überschrieben.
        </p>
      </div>

      {/* Offene Anfragen aus der App */}
      {requests.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-amber-600">Offene Anfragen aus der App:</p>
          {requests.map(r => (
            <div key={r.id} className="flex items-center justify-between gap-2 text-sm border border-amber-500/30 bg-amber-500/5 rounded-md px-3 py-1.5">
              <div className="min-w-0">
                <span className="font-medium">{r.member_name}</span>
                <span className="text-muted-foreground"> · {r.course_day} {r.course_start} · {r.course_name}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => decideRequest(r, 'bestaetigt')}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700">
                  <Check className="w-3.5 h-3.5" /> Bestätigen
                </button>
                <button onClick={() => decideRequest(r, 'abgelehnt')}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border hover:bg-muted">
                  <X className="w-3.5 h-3.5" /> Ablehnen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Anlegen */}
      <div className="flex items-start gap-2 flex-wrap">
        <div className="relative">
          <input
            value={selMember ? selMember.name : search}
            onChange={(ev) => doSearch(ev.target.value)}
            placeholder="Mitglied suchen…"
            className="border rounded-md px-3 py-1.5 text-sm bg-background w-52"
            data-testid="stammplatz-member-search"
          />
          {results.length > 0 && !selMember && (
            <div className="absolute z-10 mt-1 w-full bg-background border rounded-md shadow-md max-h-44 overflow-auto">
              {results.map(l => (
                <button key={l.id} onClick={() => { setSelMember(l); setResults([]); }}
                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-muted">
                  {l.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <select value={selCourse} onChange={(ev) => setSelCourse(ev.target.value)}
          className="border rounded-md px-3 py-1.5 text-sm bg-background"
          data-testid="stammplatz-course-select">
          <option value="">Kurs wählen…</option>
          {courseList.map(([id, c]) => (
            <option key={id} value={id}>{c.day} {c.start} · {c.course_name}</option>
          ))}
        </select>
        <button onClick={create} disabled={busy || !selMember || !selCourse}
          className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Stammplatz anlegen
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <p className="text-xs text-muted-foreground">Lädt…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">Noch keine Stammplätze vergeben.</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map(e => (
            <div key={e.id} className="flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-1.5">
              <div className="min-w-0">
                <span className={`font-medium ${e.active ? '' : 'opacity-50 line-through'}`}>{e.member_name}</span>
                <span className="text-muted-foreground"> · {e.course_day} {e.course_start} · {e.course_name}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => toggle(e)} className="text-xs text-muted-foreground hover:underline">
                  {e.active ? 'Pausieren' : 'Aktivieren'}
                </button>
                <button onClick={() => remove(e)} className="text-xs text-destructive hover:underline">
                  Beenden
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApiKeysManager() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState({});

  const getHeaders = () => ({
    ...tokenStore.getAuthHeaders(),
    'Content-Type': 'application/json',
  });

  const fetchKeys = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/keys`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setKeys(data.keys || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);
  const [neuerKey, setNeuerKey] = useState(null); // {id, key} — nur direkt nach dem Erzeugen

  const generateKey = async () => {
    try {
      const res = await fetch(`${API}/api/public/keys/generate`, {
        method: 'POST', headers: getHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Neuer API-Key erstellt — jetzt kopieren, er wird nur einmal angezeigt');
        // v2-C2: Der Klartext existiert nur in dieser Antwort — der Server
        // speichert nur den Hash. Einmalig anzeigen, bis die Seite wechselt.
        setNeuerKey({ id: data.id, key: data.key });
        setShowKey(prev => ({ ...prev, [data.id]: true }));
        fetchKeys();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch { toast.error('Netzwerkfehler'); }
  };

  const revokeKey = async (keyId) => {
    try {
      const res = await fetch(`${API}/api/public/keys/${keyId}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (res.ok) { toast.success('API-Key widerrufen'); fetchKeys(); }
    } catch { toast.error('Netzwerkfehler'); }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API-Key kopiert');
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="api-keys-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          API-Keys für die Kunden-App. Sende den Key im Header <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key</code>
        </p>
        <button
          onClick={generateKey}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          data-testid="generate-key-btn"
        >
          <Plus className="w-4 h-4" />
          Neuen Key erstellen
        </button>
      </div>

      {keys.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <Key className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-sm text-muted-foreground">Noch keine API-Keys erstellt</p>
          <p className="text-xs text-muted-foreground mt-1">Erstelle einen Key, um die Kunden-App anzubinden</p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.id} className={`bg-card border rounded-lg px-4 py-3 ${k.active ? 'border-border' : 'border-destructive/30 opacity-60'}`} data-testid={`key-${k.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${k.active ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                    <Key className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{k.name}</span>
                      {!k.active && <span className="text-[10px] bg-destructive/10 text-destructive px-1.5 py-0.5 rounded font-medium">Widerrufen</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                        {neuerKey?.id === k.id ? neuerKey.key : k.key_masked}
                      </code>
                      {neuerKey?.id === k.id ? (
                        <button onClick={() => copyKey(neuerKey.key)} className="text-primary hover:text-foreground p-0.5" data-testid={`copy-${k.id}`}>
                          <Copy className="w-3 h-3" />
                        </button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground" title="Keys werden nur gehasht gespeichert und können nicht erneut angezeigt werden">gehasht gespeichert</span>
                      )}
                      {k.ablauf && <span className="text-[10px] text-muted-foreground">läuft ab {k.ablauf}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{k.request_count || 0} Anfragen</div>
                    {k.last_used && <div className="text-[10px] text-muted-foreground">Letzte: {new Date(k.last_used).toLocaleDateString('de-DE')}</div>}
                  </div>
                  {k.active && (
                    <button
                      onClick={() => revokeKey(k.id)}
                      className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      title="Key widerrufen"
                      data-testid={`revoke-${k.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApiDocs() {
  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  const endpoints = [
    {
      method: 'GET', path: '/api/public/courses', params: '?week_date=2026-06-22', auth: 'key',
      desc: 'Alle Kurse mit Verfügbarkeit abrufen (inkl. Kategorie "infrarot")',
      response: '{ courses: [{ id, name, day, start, end, category, max_spots, booked_spots, available_spots }], week_date }'
    },
    {
      method: 'POST', path: '/api/public/book', auth: 'key',
      desc: 'Kurs buchen (auch Infrarotkabine — normale course_id, category "infrarot"). "date" ist ein Alias für "week_date" (App-Kompatibilität). Doppelbuchung → 409.',
      body: '{ "course_id": "k_mo_1", "date": "2026-06-22", "member_id": "...", "member_name": "Max" }',
      response: '{ id, booking_id, status: "confirmed", course_name, day, start, end, booked_spots, available_spots }'
    },
    {
      method: 'DELETE', path: '/api/public/book/{booking_id}', auth: 'key',
      desc: 'Buchung stornieren',
      response: '{ message: "Buchung storniert" }'
    },
    {
      method: 'GET', path: '/api/public/bookings/{member_id}', params: '?week_date=2026-06-22', auth: 'key',
      desc: 'Buchungen eines Mitglieds abrufen (rohes Array, kein Wrapper)',
      response: '[{ booking_id, course_id, course_name, date, start_time, end_time, status, waitlist_position }]'
    },
    {
      method: 'POST', path: '/api/public/checkin', auth: 'key',
      desc: 'Mitglied per App einchecken. course_id optional (frei lassen = freies Training). Körperanalyse (11.10.2026): Der Scanner liest jeden QR-Code — erkennt die App einen Code der Waage (Adresse bei inbody.com), ruft sie den Check-in gar nicht erst auf, sondern führt zur Körperanalyse. Kommt doch einer an (qr = "waage" oder der Code im Wortlaut), antwortet der Server 409 mit zustand "waagen_code" und ziel "koerperanalyse": kein Check-in, kein Tür-Nachweis.',
      body: '{ "member_id": "...", "member_name": "Max", "method": "app", "course_id": "k_mo_1" }',
      response: '{ success, total_checkins, timestamp, id, member_name, checked_in_at, already_checked_in, message }'
    },
    {
      method: 'GET', path: '/api/public/checkin/{member_id}', params: '?include_stats=true (optional)', auth: 'key',
      desc: 'Check-in-Verlauf — standardmäßig rohes Array [{id, timestamp, course_name}]. Mit ?include_stats=true stattdessen {checkins, total, stats: {today, week, month, streak}}.',
      response: '[{ id, timestamp, course_name }]  ODER  { checkins, total, stats }'
    },
    {
      method: 'GET', path: '/api/public/membership/{member_id}', auth: 'key',
      desc: 'Mitgliedschaftsstatus — flach (type/valid_until/has_starter_kit/status für die App) UND verschachtelt (active_membership/history/available_plans/is_active) in derselben Antwort.',
      response: '{ type: "basic"|"premium"|"elite", valid_until, has_starter_kit, status: "active"|"expired"|"paused", active_membership, history, available_plans, is_active }'
    },
    {
      method: 'GET', path: '/api/public/member/{member_id}', auth: 'key',
      desc: 'Mitgliederprofil — flach (full_name/belt_color/training_count/member_since/push_token/role für die App) UND verschachtelt (member/membership/...) in derselben Antwort. role ist null, außer Staff hat es explizit für dieses Mitglied gesetzt (App-seitiger PIN-Bypass, kein CRM-Auth-Ersatz).',
      response: '{ id, full_name, email, belt_color, training_count, member_since, push_token, role, member: {...}, membership, checked_in_today, total_checkins, current_week_bookings }'
    },
    {
      method: 'GET', path: '/api/public/member/email/{email}', auth: 'key',
      desc: 'Mitglied per E-Mail suchen (case-insensitive) — für den E-Mail-Login der App. Gleiche Antwort wie /member/{id}.',
      response: 'wie /member/{member_id}'
    },
    {
      method: 'POST', path: '/api/public/member/login', auth: 'key',
      desc: 'Mitglieds-Identität prüfen (member_id + letzte 4 Ziffern der Telefonnummer) und Sitzung ausstellen: kurzlebiges Access-Token (Standard 15 Min., in Header "Authorization: Bearer …" bei allen key+session-Endpunkten unten) + langlebiges, widerrufbares Refresh-Token (Standard 30 Tage). Für sensible Aktionen unten erforderlich (Tür, Trainingslog, Bestellung) — schützt davor, dass sich jemand mit dem App-Key als beliebiges Mitglied ausgibt. Rate-limitiert pro member_id und IP (5 Versuche / 15 Min.) gegen PIN-Brute-Force. Push-Token-Speicherung ist bewusst ausgenommen (siehe unten).',
      body: '{ "member_id": "lead_...", "phone_last4": "1234" }',
      response: '{ access_token, refresh_token, expires_in }'
    },
    {
      method: 'POST', path: '/api/public/member/refresh', auth: 'key',
      desc: 'Neues Access-Token gegen ein gültiges, nicht widerrufenes Refresh-Token tauschen — von der App bei jedem 401 wegen abgelaufenem Access-Token aufzurufen, statt die PIN erneut abzufragen.',
      body: '{ "refresh_token": "..." }',
      response: '{ access_token, expires_in }'
    },
    {
      method: 'POST', path: '/api/public/member/logout', auth: 'key',
      desc: 'Sitzung beenden (Refresh-Token wird widerrufen). Nimmt bewusst das Refresh-Token statt eines Sitzungs-Headers entgegen, damit Logout auch bei gerade abgelaufenem Access-Token funktioniert.',
      body: '{ "refresh_token": "..." }',
      response: '{ message: "Abgemeldet" }'
    },
    {
      method: 'POST', path: '/api/public/door/unlock', auth: 'key+session',
      desc: 'Studiotür via Nuki entriegeln. Prüft aktive Mitgliedschaft UND einen kürzlichen Check-in derselben Person (Anwesenheitsfenster, Standard 10 Min. — einstellbar unter Integrationen → Nuki). Ohne aktuellen Check-in: 403 "Bitte zuerst einchecken".',
      response: '{ message: "Tür wird geöffnet", success: true }'
    },
    {
      method: 'POST', path: '/api/public/plan/log', auth: 'key+session',
      desc: 'Trainingseinheit loggen (Kraft/Kampfsport/Frei) — löst automatischen Gürtelaufstieg aus',
      body: '{ "mode": "kraft", "dauer_min": 45, "exercises": [{ "name": "Kniebeuge", "sets": 5, "reps": 5, "weight": 100, "is_pr": true }] }',
      response: '{ ...session, belt_after }'
    },
    {
      method: 'GET', path: '/api/public/plan/{member_id}', auth: 'key+session',
      desc: 'Aktueller Gürtel, Fortschritt bis zum nächsten Gürtel, letzte PRs',
      response: '{ current_belt, total_trainings, next_belt, trainings_until_next_belt, recent_sessions, recent_prs }'
    },
    {
      method: 'GET', path: '/api/public/coaching/angebot', auth: 'key+session',
      desc: 'P1 Coaching (12.10.2026): Angebotsseite. produkte[] mit dem für DIESES Konto gültigen Preis (Mitglied oder Nicht-Mitglied), angerechnet und zu_zahlen (Plan-Check), plaetze_frei, texte (Titel, Einleitung, Ablauf, Garantie, Knopf), bedingungen_url. kauf_in_der_app ist in Phase 1 false: die App zeigt nur den Knopf „Ich will das“.',
      response: '{ mitglied, produkte: [{ schluessel, titel, kurz, preis, angerechnet, zu_zahlen, raten_moeglich, gym_inklusive }], plaetze_frei, kauf_in_der_app, texte, bedingungen_url }',
    },
    {
      method: 'POST', path: '/api/public/coaching/interesse', auth: 'key+session',
      desc: '„Ich will das“: legt dem Vertrieb eine Aufgabe an (je Konto eine offene) und schickt den Push „Wir melden uns“ (type coaching_interesse). Body: { produkt? }. Kein Kauf, keine Zahlung.',
      response: '{ ok: true, text }',
    },
    {
      method: 'GET', path: '/api/public/coaching/meine', auth: 'key+session',
      desc: 'Eigene Coaching-Pakete: Termine (nr, art termin|check, am, start, ende, status), Laufzeit (beginn, gueltig_bis, tage_rest), Coach, Testwerte (Start, Woche 5, Abschluss) und die Garantie-Quote aus dem Kraftlog. tuer_ueber_paket: true, wenn ein Nicht-Mitglied über das Paket Gym & Sauna hat (die Tür-Endpunkte antworten dann wie bei einem Mitglied mit Gym-Zugang).',
      response: '{ pakete: [{ id, name, status, termine_soll, termine_wahrgenommen, termine, beginn, gueltig_bis, tage_rest, coach, testwerte, garantie }], tuer_ueber_paket }',
    },
    {
      method: 'GET', path: '/api/public/trainingslog/heute', auth: 'key+session',
      desc: 'Kraftlog: heutige Einheiten aus freigegebenen Fitness-Zyklen des Trainingsplaners (eingecheckter Kurs zuerst), je Einheit Plan, eigener Eintrag (oder null) und letzter Wert je Übung. Pilot: Einen Zyklus bekommt nur, wer eine laufende Mitgliedschaft hat und in einem Kurs des Zyklus dabei ist (Buchung, fester Platz oder Check-in der letzten 35 Tage), dazu PT-Kunden ihren eigenen, zugewiesenen Plan (nur nach Check-Up mit bestätigter PT-Buchung) sowie Trainer und Leitung — alle anderen erhalten eine leere Liste. Zählt nicht als Training — dafür bleiben Check-in und /plan/log. `nachfrist`: Zyklen, die zu Ende sind und noch Nachträge annehmen (bis 14 Tage nach dem Ende, dieselbe Zugangsregel) — eigene Liste neben `zyklen`, damit die App nach dem letzten Zyklustag weiter zur Wochenauswahl kommt; `woche` ist die Woche, mit der sie öffnet.',
      response: '{ datum, nachfrist: [{ zyklus_id, name, wochen_gesamt, ende, frist_bis, woche, wochen: [...] }], zyklen: [{ zyklus_id, name, woche, wochen_gesamt, typ, phase, labels, eingecheckt, einheiten: [{ key, titel, plan: { haupt: { uebung, saetze, wdh, intensitaet }, zubringer: { uebungen, … }, power1, power2, conditioning: { format, dauer_min, inhalt } }, eintrag, vorwerte: { "Kniebeuge": { datum, top: { kg, wdh }, e1rm } | null } }], wochen: [{ woche, typ, montag, sonntag, offen, einheiten, eingetragen }] }] }'
    },
    {
      method: 'GET', path: '/api/public/trainingslog/zyklus/{zyklus_id}/woche/{nr}', auth: 'key+session',
      desc: 'Kraftlog: eine bestimmte Woche des Zyklus zum Eintragen oder Nachtragen (Wochenauswahl in der App — wer später einsteigt, trägt frühere Wochen nach). ALLE Einheiten der Woche mit Plan, eigenem Eintrag, letztem Wert zum Tag des Eintrags und `datum`: dem Tag, mit dem die App speichert (frühere Woche: geplanter Wochentag der Einheit; laufende Woche: ihr letzter geplanter Tag, der schon war — sonst heute; ein bestehender Eintrag behält seinen Tag). Auch für die laufende Woche gedacht: /heute zeigt an einem Tag mit geplanter Einheit nur diese, eine verpasste holt die App hierüber. `offen: false` = Frist vorbei, nur ansehen. Zugang wie beim Speichern. Fehler: 422 mit Klartext („Diese Woche hat noch nicht begonnen“, kein Zugang), 404 unbekannter Zyklus.',
      response: '{ zyklus_id, name, woche, wochen_gesamt, typ, phase, labels, montag, sonntag, heute, aktuelle_woche, offen, einheiten: [{ key, titel, plan, eintrag, datum, vorwerte }], wochen: [...] }'
    },
    {
      method: 'PUT', path: '/api/public/trainingslog', auth: 'key+session',
      desc: 'Kraftlog eintragen oder berichtigen — je Zyklus, Woche und Einheit genau ein Eintrag (zweimal senden ist harmlos). Übung muss in der Einheit geplant sein; Datum in der Zykluswoche, nicht in der Zukunft. Nachtragen geht für jede begonnene Woche, solange der Zyklus läuft und bis 14 Tage nach seinem Ende. kg 0–500, wdh ganzzahlig, rpe 1–10 in halben Schritten oder null. Nur mit Zugang zum Zyklus (siehe /heute). Fehler: 422 mit Klartext.',
      body: '{ "zyklus_id": "tz_…", "woche": 1, "einheit": "A", "datum": "2026-10-07", "bloecke": [{ "slot": "haupt", "uebung": "Kniebeuge", "saetze": [{ "kg": 80, "wdh": 8, "rpe": 7.5 }] }, { "slot": "conditioning", "ergebnis": "5 Runden + 12" }], "notiz": "" }',
      response: '{ id, zyklus_id, woche, einheit, datum, bloecke, notiz, coach_kommentar, … }'
    },
    {
      method: 'GET', path: '/api/public/trainingslog/verlauf', params: '?limit=50', auth: 'key+session',
      desc: 'Eigene Kraftlog-Einträge (neueste zuerst, mit Coach-Kommentar) und Bestwerte (geschätztes 1RM je Übung). `aenderbar` je Eintrag: das CRM nimmt eine Berichtigung noch an (Zyklus freigegeben, Nachtrag-Frist offen).',
      response: '{ eintraege: [{ …, aenderbar }], bestwerte: [{ uebung, e1rm, top: { kg, wdh }, datum }] }'
    },
    {
      method: 'DELETE', path: '/api/public/trainingslog/{id}', auth: 'key+session',
      desc: 'Eigenen Kraftlog-Eintrag löschen (fremde: 404)',
      response: '204'
    },
    {
      method: 'GET', path: '/api/public/curriculum/{belt}', auth: 'key',
      desc: 'Technik-Curriculum für einen Gürtel abrufen (staff-gepflegt)',
      response: '{ belt, items: [{ id, title, description, disziplin }] }'
    },
    {
      method: 'GET', path: '/api/public/academy/videos', params: '?category=muay_thai', auth: 'key+session',
      desc: 'P1 Akademie Videos — nur bei has_starter_kit auf der aktiven Mitgliedschaft (sonst 403)',
      response: '{ videos: [{ id, category, title, url }] }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/coaching', auth: 'key+session',
      desc: 'Ernährungscoaching zubuchbar wie PT (10.10.2026): Stand der eigenen Buchung (zustand keins | offen | laeuft | beendet | storniert) und die buchbaren Pakete. Gebucht wird über den normalen Bestell-Weg (POST /api/public/order mit der product_id); nach Bestätigung durch das Studio übernimmt ein Coach und legt das Erstgespräch fest. ALLE übrigen /api/public/ernaehrung/*-Endpunkte antworten ohne laufendes Coaching mit 403 „Ernährungscoaching nicht aktiv“ — dann diesen Endpunkt fragen und „buchen“ bzw. „verlängern“ anbieten. Ein Kauf während der Laufzeit verlängert automatisch. FUNKTIONEN (11.10.2026): funktionen schaltet einzelne Bereiche frei — genuss_planer (Wochenkarte, „Genuss einplanen“), kommentare (Coach-Kommentare im Tagebuch, seit 11.10.2026) und essensfoto (Foto statt Tippen, seit 11.10.2026 — true nur, wenn das Studio es eingeschaltet hat UND die KI-Schätzung aktiv ist). Fehlt das Feld oder ist ein Wert false, blendet die App den Bereich KOMPLETT aus (keine leeren Buttons, keine Fehlermeldung). Geschaltet wird im CRM unter Ernährung › Einstellungen, für alle Mitglieder zugleich.',
      response: '{ zustand, coaching: { id, zustand, produkt, wochen, coach_name, erstgespraech: { datum, start, ende } | null, start, ende } | null, angebote: [{ product_id, name, beschreibung, wochen, preis_brutto }], funktionen: { genuss_planer: false, kommentare: false, essensfoto: false } }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/plan', auth: 'key+session',
      desc: 'Ernährung (10.10.2026): der laufende Wochenplan des angemeldeten Mitglieds, sonst der nächste kommende; ohne Plan plan = null. Fertig gerechnet (Mengen, Nährwerte je Mahlzeit und Tag) — die App rechnet nichts nach. version für den Tausch mitnehmen. Ein Tag hat 1–4 Mahlzeiten (Vorlagen dürfen z. B. ohne Snack sein) — immer über mahlzeiten iterieren, nie vier annehmen. uhrzeit (HH:MM) je Mahlzeit über der Karte zeigen. PFLICHT (Lizenzen): Für jeden Eintrag in datenquellen zeigt die App beim Plan Name, Lizenz und Link (datenquellen[].name, lizenz, url) — BLS 4.0 (CC BY 4.0, Max Rubner-Institut) und/oder Open Food Facts (ODbL). Zutaten mit quelle = "openfoodfacts" tragen eine url zur Produktseite; quelle = "bls" kennzeichnet BLS-Werte. PREIS (11.10.2026): preis ist ein Richtpreis in Euro, bei jedem Abruf aus den aktuellen Preisen der Lebensmittel gerechnet (verbrauchte Mengen, keine Packungsgrößen) — je Mahlzeit für EINE Portion, bei personen > 1 zusätzlich gesamt (euro × personen); je Tag und am Plan die Summe der Gesamtpreise. vollstaendig = false heißt: mindestens einer Zutat fehlt der Preis (ohne nennt sie) — dann keinen Betrag zeigen. Immer mit „ca.“ anzeigen („ca. 2,15 €“). GENUSS-PLANER (11.10.2026, nur mit funktionen.genuss_planer): Jeder Tag trägt hinweis (Text oder null) — unverändert anzeigen, z. B. „Heute 150 kcal weniger, weil Samstag Pizza eingeplant ist.“ oder am Genuss-Tag „Heute Abend Pizza. Frühstück und Mittag sind schon angepasst. Genieß es!“. Betroffene Tage kommen NEU PORTIONIERT: dieselben Rezepte mit kleineren Mengen (genuss_ausgleich an der Mahlzeit = kcal weniger als ursprünglich), stand.ziel ist das Tagesziel dieses Tages (Eiweiß unverändert). Am Genuss-Tag steht an der Stelle der ersetzten Mahlzeit die Genussmahlzeit: genuss = true, genuss_id, name, symbol?, naehrwerte, rezept_id = null, zutaten = [] — nicht tauschbar (POST /tausch und /einsetzen antworten dort mit 409). Wird der Genuss gelöscht, kommt der Plan wieder wie vorher. Ohne den Schalter fehlt hinweis und nichts ändert sich.',
      response: '{ heute, plan: { id, start, ende, version, ziele: { kcal, eiweiss, fett, kh }, preis: { euro, vollstaendig, ohne: [""] }, datenquellen: [{ name, lizenz, url, lizenz_url }], vorlage: { id, name } | null, tage: [{ datum, hinweis?: "" | null, summe, preis: { euro, vollstaendig, ohne }, gegessen: [slot], stand, meal_prep?, mahlzeiten: [{ slot, uhrzeit, genuss?, genuss_id?, symbol?, genuss_ausgleich?, rezept_id, name, minuten, schwierigkeit?, budget?, bild_url?, symbolbild?, personen?, vorgekocht?, zubereitung, naehrwerte, preis: { euro, vollstaendig, ohne, gesamt? }, zutaten: [{ lebensmittel_id, name, gramm, quelle?, url? }] }] }] } | null }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/alternativen', params: '?plan_id=ep_…&datum=2026-10-12&mahlzeit=mittag&ohne=lm_ei', auth: 'key+session',
      desc: 'Bis zu 3 Ersatzmahlzeiten mit gleichen kcal, Eiweiß möglichst nah, Ausschlüsse des Mitglieds gelten. mahlzeit: fruehstueck|mittag|abend|snack. ohne (optional) = „Zutat nicht da": Rezepte mit dieser Zutat fallen weg. Jede Mahlzeit trägt preis wie im Plan (gerechnet für die Personenzahl der Stelle) — so lässt sich vor dem Tausch vergleichen.',
      response: '{ aktuell, alternativen: [Mahlzeit wie im Plan], version }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/tausch', auth: 'key+session',
      desc: 'Mahlzeit gegen eine Alternative tauschen. Nur die Rezept-ID schicken — Mengen rechnet der Server neu. 409, wenn der Plan inzwischen geändert wurde (zweites Gerät, Trainer): neu laden und erneut wählen.',
      body: '{ "plan_id": "ep_…", "datum": "2026-10-12", "mahlzeit": "mittag", "rezept_id": "rz_…", "version": 1, "ohne_lebensmittel_id": null }',
      response: '{ tag: { datum, summe, preis, mahlzeiten }, version }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/einkaufsliste', params: '?plan_id=ep_…&von=2026-10-12&bis=2026-10-14', auth: 'key+session',
      desc: 'Zutaten des Plans zusammengezählt (von/bis optional, Standard: ganzer Plan), mit Häkchen-Stand. Je Eintrag preis = Richtpreis in Euro (Menge × aktueller Preis je kg) oder null, wenn für das Lebensmittel kein Preis hinterlegt ist; preis an der Antwort ist die Summe (vollstaendig = false: ohne nennt die Einträge ohne Preis, euro zählt dann nur die übrigen). Anzeigen als „ca. 68,40 € (verbrauchte Mengen, ohne Packungsgrößen)“.',
      response: '{ plan_id, von, bis, datenquellen, preis: { euro, vollstaendig, ohne: [""] }, eintraege: [{ lebensmittel_id, name, gramm, preis: 1.23 | null, abgehakt, quelle?, url? }] }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/einkaufsliste/abhaken', auth: 'key+session',
      desc: 'Zutat in der Einkaufsliste ab- oder wieder anhaken (gilt für den ganzen Plan).',
      body: '{ "plan_id": "ep_…", "lebensmittel_id": "lm_…", "abgehakt": true }',
      response: '{ lebensmittel_id, abgehakt }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/gegessen', auth: 'key+session',
      desc: 'Mahlzeit als gegessen markieren (gegessen: false nimmt es zurück) — für Ring „Gegessen / Verbleibend“ und die Makro-Balken. Nur heute und rückwirkend (Zukunft: 400). Gezählt wird die Mahlzeit, wie sie im Plan steht; nach einem Tausch gilt die neue als nicht gegessen. Jeder Tag im Plan trägt gegessen: [slot] und stand: { ziel, geplant, gegessen, verbleibend } (je kcal, eiweiss, fett, kh).',
      body: '{ "plan_id": "ep_…", "datum": "2026-10-12", "mahlzeit": "mittag", "gegessen": true }',
      response: '{ tag: { datum, mahlzeiten, summe, preis, gegessen: ["mittag"], stand } }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/personen', auth: 'key+session',
      desc: 'Für wie viele Personen eine Mahlzeit gekocht wird (1–8, „− 1 Person +“). Zutatenmengen in der Anzeige = gramm × personen, die Einkaufsliste rechnet mit; die Nährwerte des Mitglieds bleiben gleich. bild_url (Plan, Rezeptbuch) ist relativ zur CRM-Adresse und ohne Login abrufbar. vorgekocht = „vom Vorabend“, meal_prep am Tag = heute wird nicht gekocht.',
      body: '{ "plan_id": "ep_…", "datum": "2026-10-12", "mahlzeit": "abend", "personen": 2 }',
      response: '{ tag: { … wie im Plan } }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/rezepte', auth: 'key+session',
      desc: 'Rezeptbuch des Studios: alle aktiven Rezepte mit Nährwerten und Richtpreis (preis, wie im Plan) der Grundmenge. passt = passt zu den Ausschlüssen des Mitglieds (Schalter „Ausschlüsse berücksichtigen“ in der App filtert danach), favorit = auf der Merkliste. datenquellen wie beim Plan anzeigen (Lizenzpflicht).',
      response: '{ rezepte: [{ id, name, mahlzeiten, minuten, naehrwerte, preis: { euro, vollstaendig, ohne }, zubereitung, zutaten: [{ lebensmittel_id, name, gramm }], passt, favorit }], datenquellen }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/rezepte/{rezept_id}', params: '?kcal=600', auth: 'key+session',
      desc: 'Ein Rezept mit Mengen für kcal (50–3000). Ohne kcal: passend zum Mahlzeitenziel des Mitglieds (fuer_mahlzeit), ohne Ziele die Grundportion. Je Zutat naehrwerte (ballaststoffe nur, wo bekannt), schritte nummeriert anzeigen, gewuerze als „Nach Belieben“. im_planbereich = false heißt: so stark gestreckt nimmt der Plan das Rezept nicht. preis = Richtpreis für genau diese Portion.',
      response: '{ rezept_id, name, kcal_ziel, grund_kcal, faktor, im_planbereich, fuer_mahlzeit, zutaten: [{ lebensmittel_id, name, gramm, naehrwerte: { kcal, eiweiss, kh, fett, ballaststoffe? } }], naehrwerte, ballaststoffe: { gramm, vollstaendig }, preis: { euro, vollstaendig, ohne }, schritte: [""], gewuerze: [""], bild_url, symbolbild, datenquellen }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/einsetzen', auth: 'key+session',
      desc: '„Rezept in den Plan integrieren“: Rezept aus dem Rezeptbuch an diese Mahlzeit. Vorher fragen: Optimiertes Rezept (art=optimiert: Mengen je Zutat auf Kalorien und Makros der ersetzten Mahlzeit) oder Standardrezept (art=standard: Grundmengen). 409: passt nicht zu den Ausschlüssen oder Plan inzwischen geändert (version).',
      body: '{ "plan_id": "ep_…", "datum": "2026-10-12", "mahlzeit": "mittag", "rezept_id": "rz_…", "version": 3, "art": "optimiert" }',
      response: '{ tag: { … wie im Plan }, version }'
    },
    {
      method: 'PUT', path: '/api/public/ernaehrung/favoriten', auth: 'key+session',
      desc: 'Rezept auf die Merkliste (favorit: false nimmt es herunter), höchstens 200. Alternativen beim Tauschen tragen ebenfalls favorit.',
      body: '{ "rezept_id": "rz_…", "favorit": true }',
      response: '{ rezept_id, favorit }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/plan.pdf', params: '?von=2026-10-12&bis=2026-10-18&art=plan', auth: 'key+session',
      desc: 'Ernährungsplan (art=plan) oder Einkaufsliste (art=einkauf) als PDF für den Zeitraum von–bis innerhalb des laufenden Plans (ohne Angabe: ganzer Plan). Antwort application/pdf zum Teilen/Speichern. Zeitraum außerhalb: 400, kein Plan: 404.',
      response: 'application/pdf'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/formulare', auth: 'key+session',
      desc: 'Check-ins und Formulare vom Coach: offen (auch überfällig) mit Fragen, beantwortet mit Antworten und coach_antwort. Push-Daten: typ = ernaehrung_formular (neu) bzw. ernaehrung_feedback (Coach hat geantwortet), zuweisung_id.',
      response: '{ offen: [{ id, titel, beschreibung?, minuten, faellig_am, status: "offen"|"verpasst", fragen: [{ id, typ, text, pflicht, optionen?, links?, rechts?, min?, max?, einheit? }] }], beantwortet: [{ id, titel, antworten, beantwortet_am, coach_antwort?, coach_name?, coach_antwort_am? }] }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/formulare/{zuweisung_id}', auth: 'key+session',
      desc: 'Antworten senden — je Frage-ID. Typen: skala 1–10 (Zahl), auswahl (eine Option als Text), mehrfach (Liste), ja_nein (true/false), zahl, text, gewicht (kg; landet auch im Gewichtsverlauf). Fehlende Pflichtantwort/ungültiger Wert: 400 mit Klartext; zweites Senden: 409.',
      body: '{ "antworten": { "gewicht": 69.4, "umsetzung": 8, "gut": "Meal-Prep klappt" } }',
      response: '{ id, status: "beantwortet", antworten, … }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/tagebuch', params: '?datum=2026-10-12', auth: 'key+session',
      desc: 'Tagebuch eines Tages (Standard heute): was das Mitglied außerhalb des Plans gegessen hat, Wasser, Schritte, Ziele. Der Tagesstand im Plan (stand.gegessen) zählt diese Einträge schon mit (tag.extra). datenquellen wie beim Plan anzeigen. GENUSS-PLANER (11.10.2026, nur mit funktionen.genuss_planer): hinweis (Text oder null) für diesen Tag — derselbe Satz wie in GET /woche und GET /plan; liegt ein vergangener Tag mehr als 10 % über seinem Ziel, lautet er „Kein Problem, wir verteilen das auf die nächsten Tage.“ Ohne den Schalter fehlt das Feld. KOMMENTARE (11.10.2026, nur mit funktionen.kommentare): Jeder Eintrag trägt kommentare (älteste zuerst, leer = keine) — von = coach | mitglied, name (Vorname), text, am in UTC (in Ortszeit anzeigen), gelesen (für Kommentare des Coachs: hat das Mitglied sie schon geöffnet; eigene Antworten sind immer true). Ohne den Schalter fehlt das Feld — dann keine Sprechblase zeigen. Dieselben kommentare tragen auch die Antworten von POST /tagebuch, DELETE /tagebuch/{eintrag_id}, PUT /tagebuch/wasser und /schritte. ESSENSFOTO (11.10.2026): Einträge mit art = foto tragen foto_id (Vorschaubild über GET /foto/{foto_id}?klein=1 laden — nur mit den Auth-Headern), kcal_offen (true = das Mitglied hat keine kcal angegeben, der Coach ergänzt; naehrwerte stehen dann auf 0 — „Coach schätzt noch“ zeigen statt 0 kcal), schaetzung { kcal, kcal_von, kcal_bis, sicherheit } (die Schätzung aus dem Foto, für die Spanne), genuss_id (mit dieser Genussmahlzeit verknüpft) und korrigiert_am (UTC; der Coach hat die Werte berichtigt). foto_entfernt = true heißt: Das Foto ist gelöscht (Widerruf oder Ende der Aufbewahrung), der Eintrag bleibt — dann kein Bild laden.',
      response: '{ datum, hinweis?: "" | null, eintraege: [{ id, art, mahlzeit?, name, gramm?, naehrwerte, quelle?, url?, foto_id?, kcal_offen?, schaetzung?, genuss_id?, korrigiert_am?, foto_entfernt?, kommentare?: [{ "id": "kk_1a2b3c4d", "von": "coach", "name": "Lukas", "text": "Gute Wahl! Nimm nächstes Mal noch Salat dazu.", "am": "2026-10-11T18:42:00Z", "gelesen": false }] }], summe, wasser_ml, schritte, ziele: { wasser_ml, schritte }, datenquellen }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/tagebuch', auth: 'key+session',
      desc: 'Eintrag außerhalb des Plans. art = lebensmittel (lebensmittel_id) | bls (bls_code) | barcode (barcode, Open Food Facts) — jeweils mit gramm — oder schnell (kcal, optional eiweiss/fett/kh, name). Nährwerte rechnet der Server. Heute bis 14 Tage zurück, nie Zukunft (400). Antwort: der ganze Tag. ESSENSFOTO (11.10.2026): art = foto bestätigt ein hochgeladenes Foto — eigener Eintrag „POST /tagebuch (art = foto)“ weiter unten.',
      body: '{ "art": "bls", "bls_code": "F110100", "gramm": 150, "mahlzeit": "snack" }',
      response: '{ datum, eintraege, summe, wasser_ml, schritte, ziele, datenquellen }'
    },
    {
      method: 'DELETE', path: '/api/public/ernaehrung/tagebuch/{eintrag_id}', params: '?datum=2026-10-12', auth: 'key+session',
      desc: 'Eintrag entfernen. Antwort: der ganze Tag. Kommentare zu diesem Eintrag sind danach nicht mehr abrufbar (sie zählen auch nicht mehr als ungelesen). Bei einem Foto-Eintrag wird das Foto mitgelöscht; war er mit einer Genussmahlzeit verknüpft, steht sie wieder wie geplant (danach GET /woche neu laden).',
      response: '{ datum, eintraege, … }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/foto/einwilligung', auth: 'key+session',
      desc: 'Essensfoto (11.10.2026, Stufe 3) — nur mit funktionen.essensfoto aus GET /coaching anbieten; ist das Essensfoto im Studio ausgeschaltet, antworten die Endpunkte mit 409 und detail (NICHT 403: 403 heißt „kein laufendes Coaching“ oder, bei POST /tagebuch/foto, „Einwilligung fehlt“). EINWILLIGUNG ZUERST: Vor der ersten Nutzung zeigt die App einen eigenen Bildschirm mit titel und text (unverändert, mit Absätzen) und fragt Zustimmen oder Ablehnen. Ohne Zustimmung keine Kamera — stattdessen die normale Eingabe. Die Einwilligung ist eine eigene (nicht WhatsApp, nicht Tracking, nicht Körperanalyse). Ändert das Studio den Text, kommt eine neue version und erteilt ist wieder false — dann den Bildschirm noch einmal zeigen.',
      response: '{ "erteilt": false, "version": "entwurf-2026-10", "text": "Ich willige ein, dass …", "titel": "Einwilligung: Essensfotos", "erteilt_am": null }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/foto/einwilligung', auth: 'key+session',
      desc: 'Zustimmen — mit der version, die das Mitglied gerade gelesen hat. Passt sie nicht mehr: 409 mit detail, dann GET /foto/einwilligung neu laden und den neuen Text zeigen. Wiederholbar.',
      body: '{ "version": "entwurf-2026-10" }',
      response: '{ "erteilt": true }'
    },
    {
      method: 'DELETE', path: '/api/public/ernaehrung/foto/einwilligung', auth: 'key+session',
      desc: 'Widerruf (Einstellungen › Ernährung). Der Server löscht SOFORT alle Essensfotos des Mitglieds; die Tagebuch-Einträge bleiben ohne Foto (foto_entfernt = true). Geht immer — auch ohne laufendes Coaching und wenn das Essensfoto im Studio ausgeschaltet ist (kein 403, kein 409). Danach den Bild-Cache der App leeren und das Tagebuch neu laden. geloeschte_fotos ist ein Zusatz zum Vertrag.',
      response: '{ "erteilt": false, "geloeschte_fotos": 3 }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/tagebuch/foto', auth: 'key+session',
      desc: 'Foto hochladen — multipart/form-data mit den Feldern bild (JPEG oder PNG, höchstens 8 MB; die App verkleinert vorher auf höchstens 1600 px lange Kante, JPEG 80 %), datum (Standard heute, bis 14 Tage zurück) und mahlzeit (fruehstueck | mittag | abend | snack). KEIN HEIC: iOS-Fotos vor dem Upload in JPEG wandeln (sonst 409). Der Server entfernt EXIF (auch den Aufnahmeort) und speichert das Foto verschlüsselt; an den KI-Dienst gehen nur das Bild und die Mahlzeit. Die Antwort kann bis zu 15 Sekunden dauern („Wir schauen uns dein Essen an …“). vorschlag: Gericht, Portion (klein | normal | gross), kcal mit Spanne kcal_von–kcal_bis, drei Nährwerte, sicherheit hoch | mittel | niedrig (bei niedrig „Bitte kurz prüfen“). vorschlag = null, wenn das Foto nicht erkannt wurde, die KI nicht antwortet oder aus ist — der Upload selbst gelingt trotzdem (200): dann Freitext anbieten und mit kcal: null bestätigen. genuss_id: gesetzt, wenn am selben Tag für diese Mahlzeit eine Genussmahlzeit geplant ist („Ist das deine geplante Pizza?“), sonst null. Es entsteht noch KEIN Tagebuch-Eintrag — erst mit der Bestätigung (POST /tagebuch, art = foto). Nicht bestätigte Fotos löscht der Server nach 24 Stunden. Fehler: 403 mit detail = Einwilligung fehlt (Einwilligungs-Bildschirm zeigen); 409 mit detail (nicht freigeschaltet, Bild nicht lesbar, zu groß, Tag außerhalb der 14 Tage); 429 mit detail = mehr als 10 Fotos an einem Tag. detail immer unverändert anzeigen.',
      body: 'multipart/form-data: bild=<Datei>, datum=2026-10-17, mahlzeit=abend',
      response: '{ "foto_id": "ef_9f8e7d6c-…", "vorschlag": { "name": "Pizza Salami", "portion": "normal", "kcal": 1000, "kcal_von": 900, "kcal_bis": 1100, "eiweiss_g": 42.0, "kohlenhydrate_g": 115.0, "fett_g": 38.0, "sicherheit": "mittel" } | null, "genuss_id": "gn_ab12cd34" | null }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/tagebuch', params: '(art = foto)', auth: 'key+session',
      desc: 'Foto bestätigen („Passt so“) — derselbe Endpunkt wie für jeden Tagebuch-Eintrag, mit art = foto und der foto_id aus dem Upload. name ist Pflicht; kcal, eiweiss_g, kohlenhydrate_g, fett_g wie vom Mitglied bestätigt oder geändert (kcal höchstens 5.000). kcal: null ist erlaubt (Foto nicht erkannt): Der Eintrag bekommt kcal_offen = true, der Coach ergänzt. datum und mahlzeit dürfen vom Upload abweichen (Standard: wie beim Upload). genuss_id (optional, aus der Upload-Antwort): Die Genussmahlzeit wird gegessen, und für den Wochenausgleich gelten ab dann die bestätigten kcal — danach GET /woche und den Tag neu laden. Jedes Foto lässt sich genau einmal bestätigen (zweites Mal: 409). Antwort: der ganze Tag wie bei jedem Eintrag. Fehler: 403 = Einwilligung fehlt (oder widerrufen); 404 = Foto oder Genussmahlzeit gibt es nicht (mehr); 409 mit detail (Name fehlt, schon eingetragen, Genussmahlzeit an einem anderen Tag oder schon verknüpft, nicht freigeschaltet). HINWEISTEXT unter jeder Schätzung: „Geschätzt aus dem Foto, kann um 20–30 % abweichen. Für deine Woche reicht das.“',
      body: '{ "art": "foto", "foto_id": "ef_9f8e7d6c-…", "datum": "2026-10-17", "mahlzeit": "abend", "name": "Pizza Salami", "kcal": 1000, "eiweiss_g": 42.0, "kohlenhydrate_g": 115.0, "fett_g": 38.0, "genuss_id": "gn_ab12cd34" }',
      response: '{ datum, eintraege: [{ "id": "et_…", "art": "foto", "mahlzeit": "abend", "name": "Pizza Salami", "foto_id": "ef_9f8e7d6c-…", "naehrwerte": { "kcal": 1000, "eiweiss": 42.0, "fett": 38.0, "kh": 115.0 }, "kcal_offen": false, "schaetzung": { "kcal": 1000, "kcal_von": 900, "kcal_bis": 1100, "sicherheit": "mittel" }, "genuss_id": "gn_ab12cd34", "am": "…" }], summe, wasser_ml, schritte, ziele, datenquellen }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/foto/{foto_id}', params: '?klein=1', auth: 'key+session',
      desc: 'Das eigene Foto als image/jpeg — NUR mit den Auth-Headern (X-API-Key und Authorization), es gibt keine öffentliche Adresse. Ohne klein: höchstens 1600 px lange Kante; mit klein=1 das Vorschaubild (höchstens 400 px) für die Liste im Tagebuch. Ein fremdes, gelöschtes oder unbekanntes Foto: 404. Bilder nie dauerhaft außerhalb des App-Caches speichern; nach einem Widerruf den Cache leeren.',
      response: 'image/jpeg'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/kommentare/ungelesen', auth: 'key+session',
      desc: 'Coach-Kommentare (11.10.2026, Stufe 2) — nur mit funktionen.kommentare aus GET /coaching; sonst 409 mit detail (NICHT 403: 403 heißt weiter „kein laufendes Coaching“). Für den Hinweis auf der Ernährungs-Startseite („Lukas hat 2 deiner Mahlzeiten kommentiert“): anzahl = Zahl der Tagebuch-Einträge mit mindestens einem ungelesenen Kommentar des Coachs (nicht die Zahl der Kommentare), eintraege nennt sie mit Tag. name (Zusatz zum Vertrag) ist der Vorname dessen, der zuletzt kommentiert hat, sonst null. Kommentare zu gelöschten Einträgen zählen nicht.',
      response: '{ "anzahl": 2, "eintraege": [{ "datum": "2026-10-11", "eintrag_id": "et_…" }], "name": "Lukas" }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/tagebuch/{eintrag_id}/kommentar', auth: 'key+session',
      desc: 'Antwort des Mitglieds auf einen Kommentar (oder eine eigene Frage zum Eintrag). text höchstens 500 Zeichen; datum ist der Tag des Eintrags (optional — fehlt es, findet der Server den Tag über die eintrag_id; passt es nicht zum Eintrag: 404). Wer antwortet, hat die Kommentare des Coachs an diesem Eintrag gelesen. Der Coach bekommt keinen Push und keine WhatsApp, er sieht die Antwort im Dashboard. Fehler: 409 mit detail (leer, zu lang, nicht freigeschaltet, mehr als 50 Kommentare am Eintrag), 404 wenn es den Eintrag nicht (mehr) gibt — detail unverändert anzeigen.',
      body: '{ "datum": "2026-10-11", "text": "Danke!" }',
      response: '{ "kommentar": { "id": "kk_…", "von": "mitglied", "name": "Anna", "text": "Danke!", "am": "2026-10-11T19:05:00Z", "gelesen": true } }'
    },
    {
      method: 'PUT', path: '/api/public/ernaehrung/tagebuch/{eintrag_id}/kommentare/gelesen', auth: 'key+session',
      desc: 'Beim Öffnen des Verlaufs aufrufen: Alle Kommentare des Coachs an diesem Eintrag gelten als gelesen (datum optional). Wiederholbar; danach GET /kommentare/ungelesen neu laden. PUSH bei neuem Kommentar des Coachs: data = { type: "ernaehrung_kommentar", datum, eintrag_id } (zusätzlich typ mit demselben Wert, wie bei den übrigen Ernährungs-Pushes) — die App öffnet damit direkt den Eintrag. Der Push nennt nur den Vornamen des Coachs, nie den Text des Kommentars.',
      body: '{ "datum": "2026-10-11" }',
      response: '{ "ok": true }'
    },
    {
      method: 'PUT', path: '/api/public/ernaehrung/tagebuch/wasser', auth: 'key+session',
      desc: 'Wasser des Tages in ml SETZEN (0–10.000). „+1 Glas“ rechnet die App: aktueller Wert + 250. Gleich: PUT /tagebuch/schritte mit Schritten (0–100.000).',
      body: '{ "wert": 1750, "datum": "2026-10-12" }',
      response: '{ datum, wasser_ml, schritte, ziele, … }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/lebensmittel', params: '?q=banane', auth: 'key+session',
      desc: 'Suche fürs Tagebuch: erst Lebensmittel des Studios, dann der BLS-Katalog (je_100g kcal/E/F/KH). Treffer direkt an POST /tagebuch geben (art + lebensmittel_id bzw. bls_code + gramm).',
      response: '{ treffer: [{ art: "lebensmittel"|"bls", lebensmittel_id?, bls_code?, name, je_100g, quelle }], datenquellen }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/gewicht', auth: 'key+session',
      desc: 'Gewicht eintragen (25–350 kg). Ein Wert je Tag — erneutes Senden am selben Tag überschreibt. datum optional (Standard heute, nicht in der Zukunft).',
      body: '{ "kg": 72.4, "datum": "2026-10-12" }',
      response: '{ lead_id, datum, kg, quelle: "app" }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/fortschritt', auth: 'key+session',
      desc: 'Gewichtsverlauf und Trainings der letzten 14 Tage (aus den Check-ins). ampel ist für den Trainer gedacht — in der App lieber nur die Zahlen zeigen.',
      response: '{ trainings_14_tage, gewicht: { erster, letzter, veraenderung_kg, punkte: [{ datum, kg }] }, ziel, ampel, gruende }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/woche', params: '?datum=2026-10-12', auth: 'key+session',
      desc: 'Genuss-Planer (11.10.2026, Stufe 1) — nur mit funktionen.genuss_planer aus GET /coaching; sonst 409 mit detail (NICHT 403: 403 heißt weiter „kein laufendes Coaching“). Die Woche Montag bis Sonntag (Europe/Berlin), in der datum liegt; ohne datum die laufende. budget_kcal = Summe der Tagesziele der sieben Tage (ohne Ausgleich), gegessen_kcal = bisher gegessen, geplant_kcal = was von heute an laut Tagesziel noch offen ist, frei_kcal = Rest des Budgets (nie negativ). Je Tag: basis_kcal (Tagesziel), ausgleich_kcal (negativ = weniger wegen eines Genusses oder eines ungeplanten Mehr; am Genuss-Tag selbst positiv), ziel_kcal = basis + ausgleich, gegessen_kcal, eiweiss_ziel_g (bleibt immer gleich), genuss_ids und hinweis (Text oder null, unverändert anzeigen). genuss: die Genussmahlzeiten der Woche, status geplant | gegessen | verworfen. Ist eine Genussmahlzeit über ein bestätigtes Essensfoto mit einem Tagebuch-Eintrag verknüpft (Stufe 3), trägt sie zusätzlich tagebuch_eintrag_id, kcal_geplant und kcal_bestaetigt (null, solange der Coach die kcal noch ergänzt); kcal sind dann die bestätigten. Die App rechnet nichts selbst — nach jeder Änderung (Genuss, Tagebuch) /woche und den Tag neu laden. Ungeplantes Mehr: Liegt ein vergangener Tag mehr als 10 % über seinem Ziel, verteilt der Server den Überschuss auf die späteren Tage der Woche (Hinweis „Kein Problem, wir verteilen das auf die nächsten Tage.“); am Sonntag verfällt der Rest. Ohne Tagesziel: 409 mit detail.',
      response: '{ "von": "2026-10-12", "bis": "2026-10-18", "budget_kcal": 14700, "gegessen_kcal": 6120, "geplant_kcal": 8350, "frei_kcal": 230, "tage": [{ "datum": "2026-10-12", "basis_kcal": 2100, "ausgleich_kcal": -150, "ziel_kcal": 1950, "gegessen_kcal": 1890, "eiweiss_ziel_g": 150.0, "genuss_ids": [], "hinweis": "Heute 150 kcal weniger, weil Samstag Pizza eingeplant ist." }], "genuss": [{ "id": "gn_…", "datum": "2026-10-17", "mahlzeit": "abend", "name": "Pizza Salami", "vorlage_id": "pizza", "portion": "normal", "kcal": 1060, "status": "geplant", "tatsaechlich": null, "mehr_kcal": 430, "zwei_wochen": false, "symbol": "🍕" }] }'
    },
    {
      method: 'GET', path: '/api/public/ernaehrung/genuss/vorlagen', auth: 'key+session',
      desc: 'Kacheln für „Genuss einplanen“: Pizza, Döner, Burger, Pasta, Sushi, Grillabend, Geschäftsessen, Geburtstag mit Kuchen. Reihenfolge der Liste = Anzeigereihenfolge; „Etwas anderes“ (Name + geschätzte kcal) ergänzt die App selbst. portionen nennt die kcal für klein | normal | gross, die Nährwerte gelten für normal. Werte aus dem Bundeslebensmittelschlüssel (BLS 4.0, Max Rubner-Institut) abgeleitet.',
      response: '{ "vorlagen": [{ "id": "pizza", "name": "Pizza", "symbol": "🍕", "portionen": { "klein": 740, "normal": 1060, "gross": 1380 }, "eiweiss_g": 44.2, "kohlenhydrate_g": 78.0, "fett_g": 61.6 }] }'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/genuss/vorschau', auth: 'key+session',
      desc: 'Rechnet durch, ob und wie der Genuss in die Woche passt — speichert NICHTS. Mit Vorlage: datum, mahlzeit, vorlage_id, portion (klein | normal | gross; optional name, z. B. „Pizza Salami“). Frei: datum, mahlzeit, name, kcal (50–5.000). datum: heute bis 13 Tage voraus. Antwort: kcal, mehr_kcal (mehr als die ersetzte Mahlzeit), am_tag (um wie viel die übrigen Mahlzeiten des Genuss-Tags leichter werden, höchstens 30 %), ausgleich (kcal weniger je Tag — nur künftige Tage, nie der Genuss-Tag, je Tag höchstens 10 % und nie unter die Untergrenze) und hinweis (unverändert anzeigen). Bei passt = false zeigt die App die vorschlaege als Buttons und schickt den Tipp direkt neu in die Vorschau: { portion } ersetzt die Portion, { zwei_wochen: true } wird mitgeschickt (der Rest liegt dann auf den ersten Tagen der Folgewoche). Höchstens 2 Genussmahlzeiten je Woche (je Coaching im CRM änderbar) — dann passt = false ohne Vorschläge. Fehler: 409 mit detail (Tag vorbei, zu weit voraus, Mahlzeit schon belegt, Coaching endet vorher, kein Tagesziel) — detail unverändert anzeigen.',
      body: '{ "datum": "2026-10-17", "mahlzeit": "abend", "vorlage_id": "pizza", "portion": "normal" }   oder   { "datum": "2026-10-17", "mahlzeit": "abend", "name": "Omas Geburtstag", "kcal": 900 }',
      response: '{ "passt": true, "kcal": 1060, "mehr_kcal": 430, "ausgleich": [{ "datum": "2026-10-13", "kcal": -86 }, { "datum": "2026-10-14", "kcal": -86 }], "am_tag": { "fruehstueck": -120, "mittag": -180 }, "hinweis": "Pizza am Samstag passt. Dafür Di–Fr je ca. 85 kcal weniger. Frühstück und Mittag am Samstag fallen leichter aus.", "vorschlaege": [], "zwei_wochen": false }   —   bei passt = false z. B. "hinweis": "Etwas viel für eine Woche. Mit kleiner Portion passt es, oder verteile auf zwei Wochen.", "vorschlaege": [{ "text": "Mit kleiner Portion", "portion": "klein" }, { "text": "Auf zwei Wochen verteilen", "zwei_wochen": true }]'
    },
    {
      method: 'POST', path: '/api/public/ernaehrung/genuss', auth: 'key+session',
      desc: 'Einplanen — gleicher Body wie die Vorschau (+ optional "zwei_wochen": true). Der Server rechnet noch einmal; passt es nicht, 409 mit dem Hinweis der Vorschau als detail. Je Tag und Mahlzeit nur ein geplanter Genuss (zweiter Versuch: 409). Antwort: der Genuss und die Woche, in der er liegt (wie GET /woche). Danach GET /plan neu laden: Die betroffenen Tage sind neu portioniert.',
      body: '{ "datum": "2026-10-17", "mahlzeit": "abend", "vorlage_id": "pizza", "portion": "normal" }',
      response: '{ "genuss": { id, datum, mahlzeit, name, vorlage_id, portion, kcal, status: "geplant", tatsaechlich: null, mehr_kcal, zwei_wochen, symbol }, "woche": { …wie GET /woche } }'
    },
    {
      method: 'PUT', path: '/api/public/ernaehrung/genuss/{id}', auth: 'key+session',
      desc: 'Ändern: datum, mahlzeit, portion (nur bei Vorlagen) oder kcal — nur solange der Genuss geplant ist und sein Tag nicht vorbei; der Server rechnet neu wie beim Einplanen (passt es nicht: 409 mit detail, nichts geändert). ODER nach dem Genuss-Tag die Rückmeldung „Wie war\'s?“: tatsaechlich = weniger | wie_geplant | mehr (frühestens am Tag selbst, optional, nie Pflicht) — der Genuss gilt dann als gegessen. Ohne Rückmeldung bleibt ein vergangener Genuss geplant und zählt mit seinen kcal als gegessen. Antwort wie beim Einplanen.',
      body: '{ "portion": "klein" }   oder   { "tatsaechlich": "wie_geplant" }',
      response: '{ "genuss": { … }, "woche": { …wie GET /woche } }'
    },
    {
      method: 'DELETE', path: '/api/public/ernaehrung/genuss/{id}', auth: 'key+session',
      desc: 'Löschen macht den Ausgleich rückgängig: Woche und Plan stehen wieder wie vorher. Geht, solange die Woche des Genusses nicht vorbei ist (sonst 409 mit detail); unbekannte oder fremde id: 404.',
      response: '{ "ok": true, "woche": { …wie GET /woche } }'
    },
    {
      method: 'GET', path: '/api/public/koerperanalyse', auth: 'key+session',
      desc: 'Körperanalyse (InBody, 11.10.2026) — Vertrag für die App: docs/app-koerperanalyse.md. Zustand für den Bildschirm, immer dieselben Felder: aktiv (Schalter des Studios), darf (sonst grund + text), einwilligung (noetig → Text und Version zeigen und zustimmen lassen; widerrufbar → „zurücknehmen“ anbieten), eigene Messungen neueste zuerst mit fertigen Zeilen (Name, Text, Vermerk „innerhalb der Messunschärfe“ — dazu einmal vermerk_erklaerung zeigen) und fertiger Rückfrage zum Löschen, Fragen zu den Messbedingungen, Grenzen der Handeingabe. NICHT hinter der Coaching-Sperre der Ernährung: Der Bildschirm gehört in den Reiter Profil. Bei aktiv = false wird nichts angenommen, aber Messungen und widerrufbar kommen weiter (ansehen, löschen, widerrufen; text der Einwilligung dann null). Messwerte sind Gesundheitsdaten: nichts auf dem Gerät vorhalten. Kontrollmessung (Check-Up, 11.10.2026): Die Messung am Tag eines im CRM eingetragenen Check-Ups trägt anlass = "checkup" mit anlass_text und checkup_tag (sonst "frei", null, null); der Block kontrolle bringt den fertigen Vergleich seit dem letzten und seit dem ersten Check-Up (zeilen mit name, text wie „−1,8 kg“ und vermerk „innerhalb der Messunschärfe“) oder einen Satz in text — die App rechnet nichts nach und zeigt keine Farben oder Pfeile. kontrolle ist null bei aktiv = false. Wer nur wegen unbekanntem Alter nicht darf (grund "alter_unbekannt"), bekommt den Eintrag mit dem Satz aus text, ohne Knöpfe.',
      response: '{ aktiv, darf, grund, text, einwilligung: { noetig, erteilt, erteilt_am, widerrufbar, titel, version, text }, messungen: [{ id, gemessen_am, titel, quelle, quelle_text, zeilen: [{ feld, name, wert, text, vermerk }], bedingungen, vermerke_messung, vergleichbar, loeschen_frage, anlass, anlass_text, checkup_tag }], kontrolle: { anzahl, kurz, text, letzte: { id, checkup_tag, titel }, seit_letztem: { titel, zeilen: [{ feld, name, text, vermerk }], hinweis }, seit_erstem } | null, hinweis, vermerk_erklaerung, fragen: [{ schluessel, text, antworten }], grenzen, scan: { hoechstens_alt_minuten, zeichen_max } }'
    },
    {
      method: 'POST', path: '/api/public/koerperanalyse/einwilligung', auth: 'key+session',
      desc: 'Der gelesenen Fassung zustimmen (version aus dem Zustand; 409 einwilligung_geaendert, wenn der Text inzwischen ein anderer ist). DELETE auf demselben Pfad nimmt die Einwilligung zurück (geht immer, wenn widerrufbar: auch bei ausgeschaltetem Schalter, nach Coaching-Ende und für eine frühere Fassung): Ab dann wird nichts mehr angenommen, vorhandene Messungen bleiben, bis das Mitglied sie löscht. Antwort beider: der neue Zustand.',
      body: '{ "version": "entwurf-1" }',
      response: 'wie GET /api/public/koerperanalyse'
    },
    {
      method: 'POST', path: '/api/public/koerperanalyse/scan', auth: 'key+session',
      desc: 'Ergebnis-Code der Waage, zwei Schritte mit demselben Aufruf: ohne bestaetigt die Vorschau (nichts gespeichert; hinweise fragen nach, wenn Größe, Geschlecht oder Alter vom Gerät nicht zum Profil passen), mit bestaetigt = true wird gespeichert. Der gescannte Text reist NUR im Rumpf, wird nie gespeichert und nie geloggt; die App hält ihn nur zwischen den zwei Schritten im Speicher und öffnet die Adresse darin nie. Ablehnungen tragen detail (1:1 anzeigen) und zustand: 403 einwilligung_noetig | aus | kein_coaching | kein_mitglied | unter_18 | alter_unbekannt · 400 nicht_lesbar | erklaer_code · 409 zu_alt | uhr_der_waage | anderes_konto | geraet_unter_18 · 429. Derselbe Code (dieselbe Waage, dieselbe Sekunde) beim selben Mitglied noch einmal: 200 mit bekannt = true. Nach dem Speichern ist hinweise immer leer.',
      body: '{ "code": "<Text des QR-Codes>", "bestaetigt": true, "bedingungen": { "trainiert_12h": "nein", "gegessen_3h": "nein" } }',
      response: '{ gespeichert, vorschau, bekannt, messung, hinweise: [""], fragen?, gewicht_im_verlauf? }'
    },
    {
      method: 'POST', path: '/api/public/koerperanalyse/messung', auth: 'key+session',
      desc: 'Werte von Hand, als JSON-Zahlen: Gewicht, Körperfettanteil und Skelettmuskelmasse sind Pflicht; grundumsatz_kcal ist freiwillig und dient als Prüfzahl (passt er nicht zu Gewicht und Körperfett: 400 passt_nicht). Kein Datum mitschicken — Messzeit ist jetzt. Dieselben drei Zahlen am selben Tag noch einmal: 200 mit bekannt = true, kein Duplikat; höchstens fünf Handeingaben je Tag (409 zu_viele). Fehlt ein Pflichtfeld oder ist etwas keine Zahl: 400 eingabe mit einem Satz (nie 422 mit der Eingabe). Eigene Messung löschen: DELETE /api/public/koerperanalyse/messungen/{id} (vorher mit loeschen_frage fragen; der Code ist danach wieder frei), alle: DELETE /api/public/koerperanalyse/messungen.',
      body: '{ "gewicht_kg": 82.0, "koerperfett_prozent": 17.8, "skelettmuskel_kg": 38.1, "grundumsatz_kcal": 1826, "groesse_cm": 180, "bedingungen": { "trainiert_12h": "nein", "gegessen_3h": "unbekannt" } }',
      response: '{ gespeichert: true, bekannt, messung, gewicht_im_verlauf }'
    },
    {
      method: 'GET', path: '/api/public/products', auth: 'key',
      desc: 'Shop-Produktkatalog mit Live-Bestand',
      response: '{ products: [{ id, name, price_gross, available, ... }] }'
    },
    {
      method: 'POST', path: '/api/public/order', auth: 'key+session',
      desc: 'Bestellung aufgeben (reserviert Bestand, Status "pending"). Für Produkte mit category="personal_training" entsteht automatisch ein offener PT-Auftrag, sobald ein Mitarbeiter die Bestellung im CRM bestätigt — die App muss dafür nichts Zusätzliches tun, nur den normalen Bestell-Flow verwenden.',
      body: '{ "items": [{ "product_id": "prod_...", "quantity": 1 }], "payment_method": "stripe" }',
      response: '{ ...order }'
    },
    {
      method: 'GET', path: '/api/public/pt-orders/mine', auth: 'key+session',
      desc: 'Alle eigenen PT-Aufträge (neueste zuerst) — damit die App nach dem Kauf selbst die passende pt_order_id findet, ohne dass die Bestell-Antwort dafür geparst werden muss.',
      response: '{ pt_orders: [{ id, product_name, status, trainer_name, scheduled_date, scheduled_start, scheduled_end }] }'
    },
    {
      method: 'GET', path: '/api/public/pt-order/{pt_order_id}', auth: 'key+session',
      desc: 'Status eines einzelnen eigenen PT-Auftrags. Liefert 404, falls der Auftrag nicht dem Mitglied der Session gehört.',
      response: '{ id, product_name, status: "offen"|"zugewiesen"|"abgeschlossen"|"storniert", trainer_name, scheduled_date, scheduled_start, scheduled_end }'
    },
    {
      method: 'POST', path: '/api/public/push-token', auth: 'key',
      desc: 'Push-Token für dieses Gerät speichern (Versand selbst noch nicht implementiert). Nur API-Key nötig, kein Sitzungstoken — geringes Risiko (nur Zustellungsziel), daher bewusst einfacher gehalten als Tür/Trainingslog/Bestellung.',
      body: '{ "member_id": "...", "push_token": "...", "platform": "ios" }',
      response: '{ message: "Push-Token gespeichert" }'
    },
    {
      method: 'GET', path: '/api/public/admin/today', auth: 'staff',
      desc: 'Trainer-Bereich: Tagesplan mit Buchungen pro Kurs. Erfordert CRM-Login (nicht den App-Key) + Berechtigung "app.trainer_access".',
      response: '{ date, courses: [{ course_id, name, start, end, bookings: [...] }] }'
    },
    {
      method: 'GET', path: '/api/public/admin/members', params: '?search=Schmidt', auth: 'staff',
      desc: 'Trainer-Bereich: Mitgliedersuche',
      response: '{ members: [{ id, name, email, phone, belt }] }'
    },
    {
      method: 'POST', path: '/api/public/admin/checkin/{member_id}', auth: 'staff',
      desc: 'Trainer-Bereich: Mitglied manuell einchecken (QR-Scanner/Empfang)',
      response: '{ ...checkin, already_checked_in, message }'
    },
  ];

  const methodColor = {
    GET: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    POST: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    DELETE: 'bg-red-500/10 text-red-600 border-red-500/20',
  };

  const authLabel = {
    key: 'X-API-Key',
    'key+session': 'X-API-Key + Mitglieds-Session',
    staff: 'CRM-Login (Trainer/Admin)',
  };
  const authColor = {
    key: 'bg-muted text-muted-foreground',
    'key+session': 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    staff: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
  };

  return (
    <div className="space-y-4" data-testid="api-docs">
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 space-y-2">
        <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">Authentifizierung — drei Stufen</h3>
        <p className="text-xs text-amber-600 dark:text-amber-400/80">
          Alle Endpunkte erfordern zunächst den App-Key im Header:
        </p>
        <code className="block text-xs font-mono bg-black/5 dark:bg-white/5 px-3 py-2 rounded">
          X-API-Key: pc_dein_api_key_hier
        </code>
        <p className="text-xs text-amber-600 dark:text-amber-400/80">
          Endpunkte, die physischen Zugang steuern oder eine Bestellung/ein Trainingslog anlegen (Tür, Trainingslog, Bestellung), verlangen zusätzlich das Access-Token aus <code>/member/login</code> (Standard-Gültigkeit 15 Minuten — bei Ablauf mit <code>/member/refresh</code> erneuern, nicht erneut die PIN abfragen):
        </p>
        <code className="block text-xs font-mono bg-black/5 dark:bg-white/5 px-3 py-2 rounded">
          Authorization: Bearer dein_access_token
        </code>
        <p className="text-xs text-amber-600 dark:text-amber-400/80">
          Der Trainer-/Admin-Bereich (<code>/admin/*</code>) läuft über den normalen CRM-Login der Mitarbeiter, nicht über den App-Key — Trainer haben bereits Konten mit Rollen im CRM.
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-1">Base URL</h3>
        <code className="text-xs font-mono bg-muted px-3 py-1.5 rounded block">{baseUrl}</code>
      </div>

      <div className="space-y-3">
        {endpoints.map((ep, i) => (
          <div key={`${ep.method}-${ep.path}`} className="bg-card border border-border rounded-lg overflow-hidden" data-testid={`endpoint-${i}`}>
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 bg-muted/20">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${methodColor[ep.method]}`}>
                {ep.method}
              </span>
              <code className="text-sm font-mono font-medium">{ep.path}</code>
              {ep.params && <span className="text-xs text-muted-foreground">{ep.params}</span>}
              <span className={`ml-auto text-[9px] font-medium px-2 py-0.5 rounded border ${authColor[ep.auth]}`}>
                {authLabel[ep.auth]}
              </span>
            </div>
            <div className="px-4 py-3 space-y-2">
              <p className="text-sm text-muted-foreground">{ep.desc}</p>
              {ep.body && (
                <div>
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase">Body:</span>
                  <pre className="text-xs font-mono bg-muted/50 px-3 py-2 rounded mt-1 overflow-x-auto">{ep.body}</pre>
                </div>
              )}
              <div>
                <span className="text-[10px] font-semibold text-muted-foreground uppercase">Response:</span>
                <pre className="text-xs font-mono bg-muted/50 px-3 py-2 rounded mt-1 overflow-x-auto">{ep.response}</pre>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
